import React, { useState, useEffect, useRef } from 'react';
import { StatusBar } from 'expo-status-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Animated,
  StyleSheet,
  Text,
  View,
  Image,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  TextInput,
  Platform,
} from 'react-native';
import { useFonts, PlayfairDisplay_400Regular, PlayfairDisplay_700Bold } from '@expo-google-fonts/playfair-display';

const predictions = [
  'A surprise is headed your way. Stay ready.',
  'Someone is thinking about you today.',
  'The answer will arrive when you least expect it.',
  'Trust your intuition, follow what feels right.',
  'A creative idea will open a new path for you.',
  'Good news is coming from an unexpected source.',
  'You have the strength to handle what’s next.',
  'Take a small risk — it will lead to growth.',
  'A peaceful moment is close by. Breathe deeply.',
  'The universe is aligning in your favor.',
  'A delayed plan begins moving in your favor this week.',
  'An old door closes so a better one can open quickly.',
  'Your next yes brings unexpected momentum.',
  'A meaningful conversation clears the air.',
  'Your patience is about to pay off in a visible way.',
  'A chance meeting carries an important clue.',
  'A dream you set aside is ready for your attention again.',
  'Your confidence draws the right opportunities closer.',
  'A small act of courage changes your direction for the better.',
  'You are closer to your goal than you think.',
  'The support you need is already around you.',
  'A fresh start arrives through a simple decision.',
  'Your energy is magnetic today. Use it wisely.',
  'A lucky break appears when you stay flexible.',
  'You are being guided toward a healthier boundary.',
  'A heartfelt request receives a positive response.',
  'Your creativity solves a problem faster than expected.',
  'A clear sign confirms you are on the right track.',
  'Something you lost returns in a better form.',
  'The next 24 hours bring helpful clarity.',
  'A bold idea turns into practical progress.',
  'Your calm presence helps others and strengthens your path.',
  'A new connection opens a promising chapter.',
  'Trust the timing. What is meant for you is approaching now.',
];

const DAILY_FORTUNE_KEY = 'dailyFortuneUnlockedAt';
const ANGEL_NUMBER_KEY = 'angelNumberUnlockedAt';
const DAILY_FORTUNE_RESULT_KEY = 'dailyFortuneResult';
const ANGEL_NUMBER_RESULT_KEY = 'angelNumberResult';
const COOLDOWN_MS = 24 * 60 * 60 * 1000;
const WEB_CHECKOUT_PATH = '/.netlify/functions/create-checkout-session';
const IOS_PAYMENT_NOTICE = 'Purchases are temporarily unavailable on iOS while Apple in-app purchase is being finalized.';

const angelNumbers = ['1111', '222', '333', '444', '555', '777', '888', '999', '1212', '4444'];
const angelNumberMeanings = {
  '1111': 'A new beginning is unfolding. Stay open to fresh opportunities.',
  '222': 'Balance is calling. Trust the timing and stay centered.',
  '333': 'Your spirit guides are supporting you. Express your creativity.',
  '444': 'Stability and protection surround you. Keep building your foundation.',
  '555': 'Change is approaching. Embrace transformation with confidence.',
  '777': 'You are aligned with wisdom. Keep following your inner truth.',
  '888': 'Abundance is flowing your way. Focus on what you value most.',
  '999': 'Completion is here. Release the old to make room for the new.',
  '1212': 'You are on the right path. Stay positive and keep moving forward.',
  '4444': 'Support is strong. Trust that every step is guided and secure.',
};

const lifePathMeanings = {
  1: 'Your life path points toward leadership, independence and brave new beginnings. You are meant to trust your own vision, take initiative and inspire others by moving forward with confidence.',
  2: 'Your life path is deeply connected to partnership, sensitivity and balance. You thrive when you build trust, listen to intuition and create harmony in the spaces and relationships around you.',
  3: 'Your life path carries the energy of expression, creativity and joy. You are here to communicate, uplift others and let your natural imagination become part of your purpose.',
  4: 'Your life path centers on stability, discipline and building strong foundations. You are meant to create lasting results through patience, commitment and practical wisdom.',
  5: 'Your life path is linked to freedom, change and adventure. You are here to evolve, explore new experiences and grow by trusting movement rather than fear.',
  6: 'Your life path highlights care, harmony and responsibility. You are meant to nurture, protect and bring healing energy into your home, relationships and community.',
  7: 'Your life path is rooted in intuition, study and spiritual awareness. You are here to seek truth, trust your inner voice and develop wisdom through reflection and deeper understanding.',
  8: 'Your life path speaks to abundance, authority and personal power. You are meant to learn how to lead, build security and use ambition in a way that creates meaningful success.',
  9: 'Your life path carries the vibration of completion, compassion and service. You are here to heal, release what no longer belongs to you and make a lasting impact through generosity and heart.',
};

const expressionMeanings = {
  1: 'a bold, pioneering self-expression.',
  2: 'a gentle, cooperative energy.',
  3: 'a sparkling creative personality.',
  4: 'a practical and reliable nature.',
  5: 'an adventurous, flexible spirit.',
  6: 'a nurturing and service-oriented heart.',
  7: 'a thoughtful, introspective mind.',
  8: 'an ambitious, success-driven presence.',
  9: 'a compassionate, giving character.',
};

export default function App() {
  const [prediction, setPrediction] = useState('');
  const [fortuneRevealed, setFortuneRevealed] = useState(false);
  const [fortuneLockedUntil, setFortuneLockedUntil] = useState(0);
  const [fortuneCooldownLabel, setFortuneCooldownLabel] = useState('');
  const [history, setHistory] = useState([]);
  const [angelNumber, setAngelNumber] = useState('');
  const [angelNumberMeaning, setAngelNumberMeaning] = useState('');
  const [angelLockedUntil, setAngelLockedUntil] = useState(0);
  const [angelCooldownLabel, setAngelCooldownLabel] = useState('');
  const [fontsLoaded] = useFonts({
    PlayfairDisplay_400Regular,
    PlayfairDisplay_700Bold,
  });
  const starPulse = useRef(new Animated.Value(0.55)).current;
  const glowPulse = useRef(new Animated.Value(0.25)).current;
  const predictionOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.parallel([
        Animated.sequence([
          Animated.timing(starPulse, {
            toValue: 0.95,
            duration: 1400,
            useNativeDriver: true,
          }),
          Animated.timing(starPulse, {
            toValue: 0.5,
            duration: 1400,
            useNativeDriver: true,
          }),
        ]),
        Animated.sequence([
          Animated.timing(glowPulse, {
            toValue: 0.45,
            duration: 1800,
            useNativeDriver: true,
          }),
          Animated.timing(glowPulse, {
            toValue: 0.18,
            duration: 1800,
            useNativeDriver: true,
          }),
        ]),
      ]),
    ).start();
  }, [starPulse, glowPulse]);

  useEffect(() => {
    const loadCooldowns = async () => {
      try {
        const [fortuneRaw, angelRaw] = await Promise.all([
          AsyncStorage.getItem(DAILY_FORTUNE_KEY),
          AsyncStorage.getItem(ANGEL_NUMBER_KEY),
        ]);

        const now = Date.now();
        const storedFortune = fortuneRaw ? Number(fortuneRaw) : 0;
        const storedAngel = angelRaw ? Number(angelRaw) : 0;

        const [fortuneResultRaw, angelResultRaw] = await Promise.all([
          AsyncStorage.getItem(DAILY_FORTUNE_RESULT_KEY),
          AsyncStorage.getItem(ANGEL_NUMBER_RESULT_KEY),
        ]);

        if (storedFortune > now && fortuneResultRaw) {
          setFortuneLockedUntil(storedFortune);
        } else if (storedFortune > now && !fortuneResultRaw) {
          setFortuneLockedUntil(0);
          await AsyncStorage.removeItem(DAILY_FORTUNE_KEY);
        }

        if (storedAngel > now && angelResultRaw) {
          setAngelLockedUntil(storedAngel);
        } else if (storedAngel > now && !angelResultRaw) {
          setAngelLockedUntil(0);
          await AsyncStorage.removeItem(ANGEL_NUMBER_KEY);
        }

        if (fortuneResultRaw) {
          const parsedFortune = JSON.parse(fortuneResultRaw);
          if (parsedFortune && parsedFortune.prediction) {
            setPrediction(parsedFortune.prediction);
            setFortuneRevealed(true);
            predictionOpacity.setValue(1);
          }
        }

        if (angelResultRaw) {
          const parsedAngel = JSON.parse(angelResultRaw);
          if (parsedAngel && parsedAngel.angelNumber) {
            setAngelNumber(parsedAngel.angelNumber);
            setAngelNumberMeaning(parsedAngel.angelNumberMeaning || 'This angel number carries a special message for you.');
          }
        }
      } catch (error) {
        // Ignore storage read issues to avoid blocking app usage.
      }
    };

    loadCooldowns();
  }, []);

  useEffect(() => {
    const formatRemaining = (msRemaining) => {
      const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const tick = () => {
      const now = Date.now();

      if (fortuneLockedUntil > now) {
        setFortuneCooldownLabel(`Daily Fortune available in ${formatRemaining(fortuneLockedUntil - now)}`);
      } else {
        setFortuneCooldownLabel('');
      }

      if (angelLockedUntil > now) {
        setAngelCooldownLabel(`Angel Number available in ${formatRemaining(angelLockedUntil - now)}`);
      } else {
        setAngelCooldownLabel('');
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [fortuneLockedUntil, angelLockedUntil]);

  const revealPrediction = async () => {
    const now = Date.now();
    if (fortuneLockedUntil > now) return;

    const next = predictions[Math.floor(Math.random() * predictions.length)];
    predictionOpacity.setValue(0);
    setPrediction(next);
    setFortuneRevealed(true);
    setHistory((current) => [next, ...current].slice(0, 5));
    Animated.timing(predictionOpacity, {
      toValue: 1,
      duration: 1200,
      useNativeDriver: true,
    }).start();

    const unlockAt = now + COOLDOWN_MS;
    setFortuneLockedUntil(unlockAt);
    try {
      await Promise.all([
        AsyncStorage.setItem(DAILY_FORTUNE_KEY, String(unlockAt)),
        AsyncStorage.setItem(
          DAILY_FORTUNE_RESULT_KEY,
          JSON.stringify({
            prediction: next,
            revealedAt: now,
          }),
        ),
      ]);
    } catch (error) {
      // Ignore storage write issues to avoid blocking app usage.
    }
  };

  const revealAngelNumber = async () => {
    const now = Date.now();
    if (angelLockedUntil > now) return;

    const next = angelNumbers[Math.floor(Math.random() * angelNumbers.length)];
    setAngelNumber(next);
    setAngelNumberMeaning(angelNumberMeanings[next] || 'This angel number carries a special message for you.');

    const unlockAt = now + COOLDOWN_MS;
    setAngelLockedUntil(unlockAt);
    try {
      await Promise.all([
        AsyncStorage.setItem(ANGEL_NUMBER_KEY, String(unlockAt)),
        AsyncStorage.setItem(
          ANGEL_NUMBER_RESULT_KEY,
          JSON.stringify({
            angelNumber: next,
            angelNumberMeaning: angelNumberMeanings[next] || 'This angel number carries a special message for you.',
            revealedAt: now,
          }),
        ),
      ]);
    } catch (error) {
      // Ignore storage write issues to avoid blocking app usage.
    }
  };

  const [purchaseStatus, setPurchaseStatus] = useState('');
  const [selectedReading, setSelectedReading] = useState('');
  const [selectedReadingDescription, setSelectedReadingDescription] = useState('');
  const [readingPurchaseStatus, setReadingPurchaseStatus] = useState('');
  const [tarotQuestion, setTarotQuestion] = useState('');
  const [tarotSpread, setTarotSpread] = useState([]);
  const [tarotStatus, setTarotStatus] = useState('');
  const [yesNoQuestion, setYesNoQuestion] = useState('');
  const [yesNoCard, setYesNoCard] = useState(null);
  const [yesNoAnswer, setYesNoAnswer] = useState('');
  const [yesNoStatus, setYesNoStatus] = useState('');
  const [loveFocus, setLoveFocus] = useState('existing');
  const [loveQuestion, setLoveQuestion] = useState('');
  const [lovePersonName, setLovePersonName] = useState('');
  const [loveSpread, setLoveSpread] = useState([]);
  const [loveStatus, setLoveStatus] = useState('');
  const [synastryYourName, setSynastryYourName] = useState('');
  const [synastryYourBirthdate, setSynastryYourBirthdate] = useState('');
  const [synastryYourBirthtime, setSynastryYourBirthtime] = useState('');
  const [synastryYourBirthplace, setSynastryYourBirthplace] = useState('');
  const [synastryPartnerName, setSynastryPartnerName] = useState('');
  const [synastryPartnerBirthdate, setSynastryPartnerBirthdate] = useState('');
  const [synastryPartnerBirthtime, setSynastryPartnerBirthtime] = useState('');
  const [synastryPartnerBirthplace, setSynastryPartnerBirthplace] = useState('');
  const [synastryStatus, setSynastryStatus] = useState('');
  const [pastLifeName, setPastLifeName] = useState('');
  const [pastLifeBirthdate, setPastLifeBirthdate] = useState('');
  const [pastLifeMemory, setPastLifeMemory] = useState('');
  const [pastLifeBirthmark, setPastLifeBirthmark] = useState('');
  const [pastLifeTravelCall, setPastLifeTravelCall] = useState('');
  const [pastLifeStatus, setPastLifeStatus] = useState('');
  const [numerologyName, setNumerologyName] = useState('');
  const [numerologyBirthdate, setNumerologyBirthdate] = useState('');
  const [numerologyResult, setNumerologyResult] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [spellType, setSpellType] = useState('');
  const [spellRequest, setSpellRequest] = useState('');
  const [spellEmail, setSpellEmail] = useState('');
  const [spellStatus, setSpellStatus] = useState('');
  const [houseReadingStatus, setHouseReadingStatus] = useState('');
  const [revealedDoors, setRevealedDoors] = useState({});
  const [roomReveal, setRoomReveal] = useState('');
  const [roomSelection, setRoomSelection] = useState('');
  const [roomStatus, setRoomStatus] = useState('');
  const [sealedOption, setSealedOption] = useState('');
  const [sealedStatus, setSealedStatus] = useState('');

  const doorOptions = [
    {
      key: 'door-moon',
      label: 'The Moon Door',
      image: require('./assets/moondoor.png'),
      reveal:
        'You are stepping through uncertainty into intuition. This path asks for trust, patience and emotional honesty before clarity arrives.',
    },
    {
      key: 'door-star',
      label: 'The Star Door',
      image: require('./assets/stardoor2.png'),
      reveal:
        'A hopeful chapter opens. Support, healing and renewed direction are available if you follow what feels genuine and spiritually aligned.',
    },
    {
      key: 'door-golden',
      label: 'The Golden Door',
      image: require('./assets/golden door.jpg'),
      reveal:
        'This is a power doorway for visibility and growth. Opportunity comes with responsibility and asks you to lead with confidence.',
    },
  ];

  const roomThemes = {
    'The Attic': 'Past influences, inherited stories and unresolved memories shaping present choices.',
    'The Garden': 'Growth cycles, emotional nourishment and what needs steady care to bloom.',
    'The Library': 'Knowledge, truth, contracts and the story you are rewriting about your future.',
    'The Bedroom': 'Intimacy, rest, boundaries and your private emotional restoration.',
    'The Observatory': 'Long-view destiny, timing and what is becoming visible from a higher perspective.',
  };

  const readingDetails = {
    'birth-love': 'A love chart reading tuned to your romantic timing, soulmate energy and emotional compatibility.',
    'birth-career': 'A career chart reading focused on your strengths, purpose and the highest path for your work life.',
    'birth-talents': 'A birth chart reading focused on hidden talents, natural gifts and your life purpose.',
    'birth-fertility': 'A fertility insight reading that gently explores timing, cycles and sacred fertility support.',
    'spirit-signs': 'A spirit baby sign reading that helps you recognize dreams, messages and subtle guidance.',
    'spirit-gender': 'A spirit baby gender essence reading to help you tune into the energy around your baby’s presence.',
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  const getRequiredDeliveryEmail = (setStatus) => {
    const normalized = customerEmail.trim().toLowerCase();
    if (!isValidEmail(normalized)) {
      setStatus('Please enter a valid delivery email to receive paid readings within 24-72 hours.');
      return null;
    }
    return normalized;
  };

  const beginWebCheckout = async (productKey, customerDetails, setStatus) => {
    const deliveryEmail = getRequiredDeliveryEmail(setStatus);
    if (!deliveryEmail) {
      return true;
    }

    if (Platform.OS === 'ios' && typeof window === 'undefined') {
      setStatus(IOS_PAYMENT_NOTICE);
      return true;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const response = await fetch(WEB_CHECKOUT_PATH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productKey,
          customerDetails: {
            ...customerDetails,
            customerEmail: deliveryEmail,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.url) {
        throw new Error(data.error || 'Unable to start checkout.');
      }

      window.location.assign(data.url);
    } catch (error) {
      setStatus('Checkout is unavailable right now. Please try again shortly.');
    }

    return true;
  };

  const tarotDeck = [
    { name: 'The Fool', image: require('./assets/tarot/fool0.png'), description: 'A new journey begins; trust your instincts and leap with curiosity.' },
    { name: 'The Magician', image: require('./assets/tarot/magician1.png'), description: 'Manifestation energy is strong; your focus can turn intention into reality.' },
    { name: 'The High Priestess', image: require('./assets/tarot/high_priestess2.png'), description: 'Intuition, hidden wisdom and inner guidance are rising to meet you.' },
    { name: 'The Empress', image: require('./assets/tarot/empress3.png'), description: 'Nurturing abundance and creativity are available when you honor your heart.' },
    { name: 'The Emperor', image: require('./assets/tarot/emperor4.png'), description: 'Structure, authority and grounded power support your next move.' },
    { name: 'The Hierophant', image: require('./assets/tarot/hierophant5.png'), description: 'Tradition, guidance and spiritual wisdom are available through a reliable source.' },
    { name: 'The Lovers', image: require('./assets/tarot/lovers6.png'), description: 'A choice of heart or partnership is highlighted; follow what feels aligned.' },
    { name: 'The Chariot', image: require('./assets/tarot/chariot7.png'), description: 'Drive forward with confidence; your will can shape the path ahead.' },
    { name: 'Strength', image: require('./assets/tarot/strength8.png'), description: 'Gentle courage and compassion are your greatest assets in this moment.' },
    { name: 'The Hermit', image: require('./assets/tarot/hermit9.png'), description: 'Quiet reflection and inner searching will reveal your next clue.' },
    { name: 'Wheel of Fortune', image: require('./assets/tarot/wheel_of_fortune10.png'), description: 'Cycles shift; embrace the flow of change and the opportunity it brings.' },
    { name: 'Justice', image: require('./assets/tarot/justice11.png'), description: 'Balance, truth and clarity are important for the decision you seek.' },
    { name: 'The Hanged Man', image: require('./assets/tarot/hanged_man12.png'), description: 'A fresh view is possible through surrendering to the pause.' },
    { name: 'Death', image: require('./assets/tarot/death13.png'), description: 'Transformation is unfolding; something old is clearing space for the new.' },
    { name: 'Temperance', image: require('./assets/tarot/temperance14.png'), description: 'Harmony and moderation will guide you toward better alignment.' },
    { name: 'The Devil', image: require('./assets/tarot/devil15.png'), description: 'Notice where attachments or fear are holding you back from your truth.' },
    { name: 'The Tower', image: require('./assets/tarot/tower16.png'), description: 'A sudden breakthrough clears unstable ground so truth can rebuild stronger.' },
    { name: 'The Star', image: require('./assets/tarot/star17.png'), description: 'Hope, healing and renewed faith are here to inspire you.' },
    { name: 'The Moon', image: require('./assets/tarot/moon18.png'), description: 'Pay attention to intuition, dreams and what is revealed beneath the surface.' },
    { name: 'The Sun', image: require('./assets/tarot/sun19.png'), description: 'Clarity, joy and success are shining on your chosen direction.' },
    { name: 'Judgment', image: require('./assets/tarot/judgment20.png'), description: 'A call to wake up and respond to your soul\'s next chapter is present.' },
    { name: 'The World', image: require('./assets/tarot/world21.png'), description: 'Completion and a sense of wholeness are arriving with your effort.' },
    { name: 'Ace of Cups', image: require('./assets/tarot/ace_cups.png'), description: 'A fresh emotional opening invites love, compassion and intuition.' },
    { name: 'Two of Cups', image: require('./assets/tarot/two_cups.png'), description: 'Mutual connection and heartfelt partnership are highlighted.' },
    { name: 'Three of Cups', image: require('./assets/tarot/three_cups.png'), description: 'Celebration, friendship and shared joy lift your spirit.' },
    { name: 'Four of Cups', image: require('./assets/tarot/four_cups.png'), description: 'Pause and reflection help you notice opportunities you missed.' },
    { name: 'Five of Cups', image: require('./assets/tarot/five_cups.png'), description: 'Healing begins when you honor grief and turn toward what remains.' },
    { name: 'Six of Cups', image: require('./assets/tarot/six_cups.png'), description: 'Nostalgia, kindness and heartfelt memories offer gentle guidance.' },
    { name: 'Seven of Cups', image: require('./assets/tarot/seven_cups.png'), description: 'Many options appear; choose the path that aligns with reality and heart.' },
    { name: 'Eight of Cups', image: require('./assets/tarot/eight_cups.png'), description: 'Leaving what no longer fulfills you makes room for deeper purpose.' },
    { name: 'Nine of Cups', image: require('./assets/tarot/nine_cups.png'), description: 'Satisfaction and emotional fulfillment are within reach.' },
    { name: 'Ten of Cups', image: require('./assets/tarot/ten_cups.png'), description: 'Harmony, family joy and emotional wholeness are blessed.' },
    { name: 'Page of Cups', image: require('./assets/tarot/page_cups.png'), description: 'A tender message or creative spark arrives with open-hearted energy.' },
    { name: 'Knight of Cups', image: require('./assets/tarot/knight_cups.png'), description: 'Follow your heart with sincerity while staying grounded.' },
    { name: 'Queen of Cups', image: require('./assets/tarot/queen_cups.png'), description: 'Compassion, intuition and emotional wisdom guide your choices.' },
    { name: 'King of Cups', image: require('./assets/tarot/king_cups.png'), description: 'Emotional mastery helps you lead with calm strength and care.' },
    { name: 'Ace of Wands', image: require('./assets/tarot/ace_wands.png'), description: 'A burst of inspiration and bold creative momentum begins now.' },
    { name: 'Two of Wands', image: require('./assets/tarot/two_wands.png'), description: 'Planning and vision help you choose your next expansion.' },
    { name: 'Three of Wands', image: require('./assets/tarot/three_wands.png'), description: 'Progress builds as your efforts start to show results.' },
    { name: 'Four of Wands', image: require('./assets/tarot/four_wands.png'), description: 'Stability, celebration and meaningful milestones are near.' },
    { name: 'Five of Wands', image: require('./assets/tarot/five_wands.png'), description: 'Competition or friction can sharpen your focus and growth.' },
    { name: 'Six of Wands', image: require('./assets/tarot/six_wands.png'), description: 'Recognition and confidence rise as your efforts are seen.' },
    { name: 'Seven of Wands', image: require('./assets/tarot/seven_wands.png'), description: 'Stand your ground and protect what matters most.' },
    { name: 'Eight of Wands', image: require('./assets/tarot/eight_wands.png'), description: 'Fast movement and swift messages bring rapid change.' },
    { name: 'Nine of Wands', image: require('./assets/tarot/nine_wands.png'), description: 'Resilience carries you through the final stretch.' },
    { name: 'Ten of Wands', image: require('./assets/tarot/ten_wands.png'), description: 'Release extra burdens and focus on what is truly yours to carry.' },
    { name: 'Page of Wands', image: require('./assets/tarot/page_wands.png'), description: 'Curiosity and courage spark an exciting new adventure.' },
    { name: 'Knight of Wands', image: require('./assets/tarot/knight_wands.png'), description: 'Bold action and passion propel you forward quickly.' },
    { name: 'Queen of Wands', image: require('./assets/tarot/queen_wands.png'), description: 'Magnetic confidence and creativity amplify your influence.' },
    { name: 'King of Wands', image: require('./assets/tarot/king_wands.png'), description: 'Visionary leadership and conviction shape strong outcomes.' },
    { name: 'Ace of Pentacles', image: require('./assets/tarot/ace_pentacles.png'), description: 'A practical new beginning brings stability and opportunity.' },
    { name: 'Two of Pentacles', image: require('./assets/tarot/two_pentacles.png'), description: 'Balance priorities wisely as life asks for adaptability.' },
    { name: 'Three of Pentacles', image: require('./assets/tarot/three_pentacles.png'), description: 'Teamwork and craftsmanship create meaningful progress.' },
    { name: 'Four of Pentacles', image: require('./assets/tarot/four_pentacles.png'), description: 'Security matters, but flexibility keeps energy flowing.' },
    { name: 'Five of Pentacles', image: require('./assets/tarot/five_pentacles.png'), description: 'Support is available even in challenging moments; do not isolate.' },
    { name: 'Six of Pentacles', image: require('./assets/tarot/six_pentacles.png'), description: 'Giving and receiving come into healthier balance.' },
    { name: 'Seven of Pentacles', image: require('./assets/tarot/seven_pentacles.png'), description: 'Patience and steady effort are maturing into reward.' },
    { name: 'Eight of Pentacles', image: require('./assets/tarot/eight_pentacles.png'), description: 'Skill-building and dedication bring mastery over time.' },
    { name: 'Nine of Pentacles', image: require('./assets/tarot/nine_pentacles.png'), description: 'Independence, refinement and earned abundance are highlighted.' },
    { name: 'Ten of Pentacles', image: require('./assets/tarot/ten_pentacles.png'), description: 'Long-term security and legacy-building are favored now.' },
    { name: 'Page of Pentacles', image: require('./assets/tarot/page_pentacles.png'), description: 'A grounded opportunity appears through study, focus and practical effort.' },
    { name: 'Knight of Pentacles', image: require('./assets/tarot/knight_pentacles.png'), description: 'Slow steady progress wins through discipline and consistency.' },
    { name: 'Queen of Pentacles', image: require('./assets/tarot/queen_pentacles.png'), description: 'Nurturing stability and resourcefulness support your next steps.' },
    { name: 'King of Pentacles', image: require('./assets/tarot/king_pentacles.png'), description: 'Long-range planning and practical leadership create secure outcomes.' },
    { name: 'Ace of Swords', image: require('./assets/tarot/ace_swords.png'), description: 'Mental clarity and truth cut through confusion.' },
    { name: 'Two of Swords', image: require('./assets/tarot/two_swords.png'), description: 'A decision point asks for calm balance and honest perspective.' },
    { name: 'Three of Swords', image: require('./assets/tarot/three_swords.png'), description: 'A heart truth may sting first yet leads to deeper healing.' },
    { name: 'Four of Swords', image: require('./assets/tarot/four_swords.png'), description: 'Rest and recovery restore your strength and clarity.' },
    { name: 'Five of Swords', image: require('./assets/tarot/five_swords.png'), description: 'Choose your battles wisely and protect your peace.' },
    { name: 'Six of Swords', image: require('./assets/tarot/six_swords.png'), description: 'A smoother chapter begins as you move beyond recent strain.' },
    { name: 'Seven of Swords', image: require('./assets/tarot/seven_swords.png'), description: 'Move strategically and protect your plans while truth catches up.' },
    { name: 'Eight of Swords', image: require('./assets/tarot/eight_swords.png'), description: 'Limiting beliefs can be released when you see your options clearly.' },
    { name: 'Nine of Swords', image: require('./assets/tarot/nine_swords.png'), description: 'Anxious thoughts ease when you bring fear into the light.' },
    { name: 'Ten of Swords', image: require('./assets/tarot/ten_swords.png'), description: 'A hard ending clears space for a cleaner and wiser beginning.' },
    { name: 'Page of Swords', image: require('./assets/tarot/page_swords.png'), description: 'Curiosity and clear communication reveal useful information.' },
    { name: 'Queen of Swords', image: require('./assets/tarot/queen_swords.png'), description: 'Discernment and direct honesty help you choose what is right.' },
    { name: 'Knight of Swords', image: require('./assets/tarot/knight_swords.png'), description: 'Decisive action and mental momentum push events forward quickly.' },
    { name: 'King of Swords', image: require('./assets/tarot/king_swords.png'), description: 'Clear judgment and authority bring order to complex choices.' },
  ];

  const purchaseReading = async (readingId) => {
    setSelectedReading(readingId);
    setSelectedReadingDescription(readingDetails[readingId] || 'A guided reading will arrive with deeper insight.');
    setReadingPurchaseStatus('Opening checkout...');

    if (
      await beginWebCheckout(
        readingId,
        { selectedReading: readingId },
        setReadingPurchaseStatus,
      )
    ) {
      return;
    }

    setReadingPurchaseStatus('Your reading purchase is confirmed. A psychic experience will be delivered by email within 24-72 hours.');
  };

  const drawUniqueCards = (count, sourceDeck = tarotDeck) => {
    const deck = [...sourceDeck];
    const cards = [];
    while (cards.length < count && deck.length > 0) {
      const index = Math.floor(Math.random() * deck.length);
      cards.push(deck.splice(index, 1)[0]);
    }
    return cards;
  };

  const getLoveRelevantDeck = (focus) => {
    const loveCore = [
      'The Lovers',
      'The Empress',
      'The Emperor',
      'The Hierophant',
      'Temperance',
      'The Star',
      'The Sun',
      'Ace of Cups',
      'Two of Cups',
      'Three of Cups',
      'Six of Cups',
      'Nine of Cups',
      'Ten of Cups',
      'Page of Cups',
      'Knight of Cups',
      'Queen of Cups',
      'King of Cups',
      'Ace of Wands',
      'Four of Wands',
      'Six of Wands',
      'Ten of Pentacles',
    ];

    const existingPersonCards = [
      'Justice',
      'Strength',
      'The Devil',
      'The Tower',
      'The Moon',
      'The Hanged Man',
      'Four of Cups',
      'Five of Cups',
      'Seven of Cups',
      'Two of Pentacles',
      'Three of Pentacles',
    ];

    const incomingLoveCards = [
      'The Fool',
      'The Magician',
      'The Chariot',
      'Wheel of Fortune',
      'Judgment',
      'The World',
      'Two of Wands',
      'Three of Wands',
      'Eight of Wands',
      'Page of Wands',
      'Knight of Wands',
      'Ace of Pentacles',
    ];

    const focusCards = focus === 'existing' ? existingPersonCards : incomingLoveCards;
    const allowedNames = new Set([...loveCore, ...focusCards]);
    const filteredDeck = tarotDeck.filter((card) => allowedNames.has(card.name));

    // Fallback guard in case future deck edits remove too many filtered cards.
    if (filteredDeck.length < 5) {
      return tarotDeck;
    }

    return filteredDeck;
  };

  const purchaseTarotSpread = async () => {
    if (!tarotQuestion.trim()) {
      setTarotStatus('Please focus on your question before buying the tarot spread.');
      return;
    }

    if (
      await beginWebCheckout(
        'tarot-spread',
        { tarotQuestion },
        setTarotStatus,
      )
    ) {
      return;
    }

    const spread = drawUniqueCards(3);
    setTarotSpread(spread);
    setTarotStatus('Your unique 3-card tarot spread appears below. Reflect on each card with your question in mind.');
  };

  const getYesNoFromCard = (cardName) => {
    const yesCards = [
      'The Sun',
      'The Star',
      'The World',
      'The Lovers',
      'The Magician',
      'The Empress',
      'The Chariot',
      'Strength',
      'Two of Cups',
      'Ten of Cups',
      'Four of Wands',
      'Six of Wands',
      'Ace of Cups',
      'Ace of Wands',
      'Ace of Pentacles',
      'Nine of Cups',
    ];
    const noCards = [
      'The Tower',
      'The Devil',
      'Five of Cups',
      'Five of Pentacles',
      'Ten of Wands',
      'Nine of Wands',
      'The Moon',
      'The Hanged Man',
      'Four of Cups',
      'Seven of Cups',
    ];

    if (yesCards.includes(cardName)) {
      return 'YES';
    }
    if (noCards.includes(cardName)) {
      return 'NO';
    }
    return 'MAYBE';
  };

  const purchaseYesNoPull = async () => {
    if (!yesNoQuestion.trim()) {
      setYesNoStatus('Please ask one clear yes or no question before purchasing this pull.');
      return;
    }

    if (
      await beginWebCheckout(
        'yes-no-pull',
        { yesNoQuestion },
        setYesNoStatus,
      )
    ) {
      return;
    }

    const [card] = drawUniqueCards(1);
    const answer = getYesNoFromCard(card.name);
    setYesNoCard(card);
    setYesNoAnswer(answer);
    setYesNoStatus('Your $1 yes or no card pull is complete.');
  };

  const purchaseLoveSpread = async () => {
    if (!loveQuestion.trim()) {
      setLoveStatus('Please enter your love question before buying the advanced spread.');
      return;
    }
    if (loveFocus === 'existing' && !lovePersonName.trim()) {
      setLoveStatus('Please add your person of interest name or initials for this reading type.');
      return;
    }

    if (
      await beginWebCheckout(
        'love-spread',
        {
          loveFocus,
          loveQuestion,
          lovePersonName,
        },
        setLoveStatus,
      )
    ) {
      return;
    }

    const positions =
      loveFocus === 'existing'
        ? [
            'Your current love energy',
            `${lovePersonName.trim()}'s energy`,
            'Connection dynamic',
            'What needs healing',
            'Near-future outcome',
          ]
        : [
            'Your current love energy',
            'Who may be approaching',
            'What to release now',
            'How to call love in',
            'Near-future love outlook',
          ];

    const loveDeck = getLoveRelevantDeck(loveFocus);
    const spread = drawUniqueCards(5, loveDeck).map((card, index) => ({
      ...card,
      position: positions[index],
    }));

    setLoveSpread(spread);
    setLoveStatus('Your $11.99 advanced love spread is ready from a curated love-focused deck. Reflect on each position in order for the clearest message.');
  };

  const purchaseMiniReading = () => {
    setPurchaseStatus('Your mini psychic reading request is confirmed. A psychic insight will arrive by email within 24-72 hours.');
  };

  const purchaseHouseReading = async (productKey) => {
    setHouseReadingStatus('Opening checkout...');

    if (
      await beginWebCheckout(
        productKey,
        { productKey },
        setHouseReadingStatus,
      )
    ) {
      return;
    }

    setHouseReadingStatus('Your house reading request is confirmed. A psychic will follow up by email within 24-72 hours.');
  };

  const chooseDoor = (door) => {
    setRevealedDoors((prev) => ({
      ...prev,
      [door.key]: !prev[door.key],
    }));
  };

  const chooseRoom = (roomName) => {
    setRoomSelection(roomName);
    setRoomReveal(roomThemes[roomName] || 'A hidden life theme is ready to be revealed.');
    setRoomStatus('Theme revealed. Tap Unlock My Fate to reveal your full room reading.');
  };

  const purchaseRoomReading = async () => {
    if (!roomSelection) {
      setRoomStatus('Choose a room first to reveal your hidden theme.');
      return;
    }

    setRoomStatus('Opening checkout...');
    if (
      await beginWebCheckout(
        'choose-room',
        { roomSelection },
        setRoomStatus,
      )
    ) {
      return;
    }

    setRoomStatus('Your room reading purchase is confirmed. Your full reading will be emailed within 24-72 hours.');
  };

  const purchaseSealedEnvelope = async () => {
    if (!sealedOption) {
      setSealedStatus('Choose when to open the envelope first.');
      return;
    }

    setSealedStatus('Opening checkout...');
    if (
      await beginWebCheckout(
        sealedOption,
        { sealedOption },
        setSealedStatus,
      )
    ) {
      return;
    }

    setSealedStatus('Your sealed envelope reading is scheduled.');
  };

  const purchasePastLifeReading = async () => {
    if (!pastLifeName || !pastLifeBirthdate || !pastLifeMemory || !pastLifeBirthmark || !pastLifeTravelCall) {
      setPastLifeStatus('Please complete all past life fields before purchasing.');
      return;
    }

    if (
      await beginWebCheckout(
        'past-life-reading',
        {
          pastLifeName,
          pastLifeBirthdate,
          pastLifeMemory,
        },
        setPastLifeStatus,
      )
    ) {
      return;
    }

    setPastLifeStatus('Your past life reading request has been submitted. A psychic will connect with your answers by email within 24-72 hours.');
  };

  const purchaseSynastryReading = async () => {
    const required = [
      synastryYourName,
      synastryYourBirthdate,
      synastryYourBirthplace,
      synastryPartnerName,
      synastryPartnerBirthdate,
      synastryPartnerBirthplace,
    ];

    if (required.some((value) => !value.trim())) {
      setSynastryStatus('Please complete both birth charts before purchasing the synastry love reading.');
      return;
    }

    if (
      await beginWebCheckout(
        'synastry-reading',
        {
          synastryYourName,
          synastryYourBirthdate,
          synastryPartnerName,
          synastryPartnerBirthdate,
        },
        setSynastryStatus,
      )
    ) {
      return;
    }

    setSynastryStatus('Your synastry love reading request is confirmed. A psychic will review both birth charts and send your compatibility insights by email within 24-72 hours.');
  };

  const calculateNumerology = async () => {
    const cleanedName = numerologyName.replace(/[^A-Za-z]/g, '').toUpperCase();
    const digits = numerologyBirthdate.replace(/[^0-9]/g, '');
    if (!cleanedName || digits.length < 6) {
      setNumerologyResult('Please enter a full name and birthdate (MM/DD/YYYY) to calculate your numerology.');
      return;
    }

    const reduceNumber = (value) => {
      let total = value;
      while (total > 9) {
        total = total.toString().split('').reduce((sum, digit) => sum + Number(digit), 0);
      }
      return total;
    };
    const lifePath = reduceNumber(digits.split('').reduce((sum, digit) => sum + Number(digit), 0));
    const expression = reduceNumber(
      cleanedName.split('').reduce((sum, char) => {
        const letterValue = ((char.charCodeAt(0) - 65) % 9) + 1;
        return sum + letterValue;
      }, 0),
    );
    setNumerologyResult(
      `Your Life Path Number is ${lifePath}. ${lifePathMeanings[lifePath]} Your Expression Number is ${expression}, which adds ${expressionMeanings[expression]} This combination suggests the way you naturally move through life is part of your deeper purpose, and your voice, personality and choices all help reveal the path you are meant to follow.`,
    );
  };

  const placeSpellOrder = () => {
    if (Platform.OS === 'ios' && typeof window === 'undefined') {
      setSpellStatus(IOS_PAYMENT_NOTICE);
      return;
    }

    if (!spellType) {
      setSpellStatus('Please select a spell type before placing your order.');
      return;
    }

    const deliveryEmail = spellEmail.trim() || customerEmail.trim();
    if (!isValidEmail(deliveryEmail)) {
      setSpellStatus('Please enter a valid email so your completed ritual updates can be sent within 24-72 hours.');
      return;
    }

    if (spellType === 'custom' && !spellRequest.trim()) {
      setSpellStatus('Please add details for your custom spell request.');
      return;
    }

    if (typeof window !== 'undefined') {
      setSpellStatus('Spell checkout still needs a final ritual-price mapping before it can go live on the website.');
      return;
    }

    setSpellStatus('Your spell order request has been received. The psychic will send any relevant information, videos or images to your email within 24-72 hours.');
  };

  const orderSpellRitual = (ritualName) => {
    setSpellType('custom');
    setSpellRequest(ritualName);
    setSpellStatus(`Selected ${ritualName}. Add your email below, then tap Activate My Spell. Completed updates are sent within 24-72 hours.`);
  };

  const clearHistory = async () => {
    setHistory([]);
    setPrediction('');
    setFortuneRevealed(false);
    setFortuneLockedUntil(0);
    setFortuneCooldownLabel('');
    setAngelNumber('');
    setAngelNumberMeaning('');
    setAngelLockedUntil(0);
    setAngelCooldownLabel('');
    setSelectedReading('');
    setSelectedReadingDescription('');
    setReadingPurchaseStatus('');
    setPurchaseStatus('');
    setTarotQuestion('');
    setTarotSpread([]);
    setTarotStatus('');
    setYesNoQuestion('');
    setYesNoCard(null);
    setYesNoAnswer('');
    setYesNoStatus('');
    setLoveFocus('existing');
    setLoveQuestion('');
    setLovePersonName('');
    setLoveSpread([]);
    setLoveStatus('');
    setSynastryYourName('');
    setSynastryYourBirthdate('');
    setSynastryYourBirthtime('');
    setSynastryYourBirthplace('');
    setSynastryPartnerName('');
    setSynastryPartnerBirthdate('');
    setSynastryPartnerBirthtime('');
    setSynastryPartnerBirthplace('');
    setSynastryStatus('');
    setPastLifeName('');
    setPastLifeBirthdate('');
    setPastLifeMemory('');
    setPastLifeBirthmark('');
    setPastLifeTravelCall('');
    setPastLifeStatus('');
    setNumerologyName('');
    setNumerologyBirthdate('');
    setNumerologyResult('');
    setCustomerEmail('');
    setSpellType('');
    setSpellRequest('');
    setSpellEmail('');
    setSpellStatus('');
    setHouseReadingStatus('');
    setRevealedDoors({});
    setRoomReveal('');
    setRoomSelection('');
    setRoomStatus('');
    setSealedOption('');
    setSealedStatus('');

    try {
      await Promise.all([
        AsyncStorage.removeItem(DAILY_FORTUNE_KEY),
        AsyncStorage.removeItem(ANGEL_NUMBER_KEY),
        AsyncStorage.removeItem(DAILY_FORTUNE_RESULT_KEY),
        AsyncStorage.removeItem(ANGEL_NUMBER_RESULT_KEY),
      ]);
    } catch (error) {
      // Ignore storage clear issues so reset still works visually.
    }
  };

  if (!fontsLoaded) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading mystical reading…</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <Animated.View style={[styles.softGlow1, { opacity: glowPulse }]} />
      <Animated.View style={[styles.backgroundGlow, { opacity: glowPulse }]} />
      <Animated.View style={[styles.star1, { opacity: starPulse }]} />
      <Animated.View style={[styles.star2, { opacity: starPulse }]} />
      <Animated.View style={[styles.star3, { opacity: starPulse }]} />
      <Animated.View style={[styles.star4, { opacity: starPulse }]} />
      <Animated.View style={[styles.star5, { opacity: starPulse }]} />
      <Animated.View style={[styles.star6, { opacity: starPulse }]} />
      <Animated.View style={[styles.star7, { opacity: starPulse }]}>
        <View style={styles.starBar} />
        <View style={[styles.starBar, styles.starBarRotated]} />
      </Animated.View>
      <Animated.View style={[styles.star8, { opacity: starPulse }]}>
        <View style={styles.starBar} />
        <View style={[styles.starBar, styles.starBarRotated]} />
      </Animated.View>
      <Animated.View style={[styles.star9, { opacity: starPulse }]} />
      <Animated.View style={[styles.star10, { opacity: starPulse }]} />
      <Animated.View style={[styles.star11, { opacity: starPulse }]} />
      <Animated.View style={[styles.star12, { opacity: starPulse }]} />
      <Animated.View style={[styles.star13, { opacity: starPulse }]} />
      <Animated.View style={[styles.star14, { opacity: starPulse }]} />
      <Animated.View style={[styles.star15, { opacity: starPulse }]} />
      <Animated.View style={[styles.star16, { opacity: starPulse }]} />
      <Animated.View style={[styles.star17, { opacity: starPulse }]} />
      <Animated.View style={[styles.star18, { opacity: starPulse }]} />
      <Animated.View style={[styles.star19, { opacity: starPulse }]} />
      <Animated.View style={[styles.star20, { opacity: starPulse }]} />
      <Animated.View style={[styles.star21, { opacity: starPulse }]} />
      <Animated.View style={[styles.star22, { opacity: starPulse }]} />
      <Animated.View style={[styles.star23, { opacity: starPulse }]} />
      <Animated.View style={[styles.star24, { opacity: starPulse }]} />
      <Animated.View style={[styles.star25, { opacity: starPulse }]} />
      <Animated.View style={[styles.star26, { opacity: starPulse }]} />
      <Animated.View style={[styles.star27, { opacity: starPulse }]} />
      <Animated.View style={[styles.star28, { opacity: starPulse }]} />
      <Animated.View style={[styles.star29, { opacity: starPulse }]} />
      <Animated.View style={[styles.star30, { opacity: starPulse }]} />
      <Animated.View style={[styles.star31, { opacity: starPulse }]} />
      <Animated.View style={[styles.star32, { opacity: starPulse }]} />
      <Animated.Text style={[styles.starShape1, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape13, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape14, { opacity: starPulse }]}>✦</Animated.Text>
      <Animated.Text style={[styles.starShape15, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape16, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape2, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape3, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape4, { opacity: starPulse }]}>✦</Animated.Text>
      <Animated.Text style={[styles.starShape5, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape6, { opacity: starPulse }]}>✦</Animated.Text>
      <Animated.Text style={[styles.starShape7, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape8, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape9, { opacity: starPulse }]}>✦</Animated.Text>
      <Animated.Text style={[styles.starShape10, { opacity: starPulse }]}>★</Animated.Text>
      <Animated.Text style={[styles.starShape11, { opacity: starPulse }]}>✦</Animated.Text>
      <Animated.Text style={[styles.starShape12, { opacity: starPulse }]}>★</Animated.Text>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.header}>
          <Image source={require('./assets/PH2.png')} style={styles.logo} />
          <Text style={styles.logoTagline}>Modern mystics. Ancient wisdom. Your answers are already waiting.</Text>
        </View>

        <View style={styles.card}>{fortuneRevealed ? (
          <View style={styles.predictionInner}>
            <Animated.View style={[styles.predictionStars, { opacity: starPulse }]}>
              {[
                ['✦', styles.predictionStarSmall],
                ['✧', styles.predictionStarTiny],
                ['★', styles.predictionStarMedium],
                ['✷', styles.predictionStarTiny],
                ['✹', styles.predictionStarSmall],
                ['✺', styles.predictionStarMedium],
              ].map(([symbol, sizeStyle], index) => (
                <Text key={`prediction-star-${index}`} style={[styles.predictionStar, sizeStyle]}>{symbol}</Text>
              ))}
            </Animated.View>
            <Animated.Text
              style={[
                styles.prediction,
                {
                  opacity: predictionOpacity,
                  transform: [
                    {
                      translateY: predictionOpacity.interpolate({
                        inputRange: [0, 1],
                        outputRange: [12, 0],
                      }),
                    },
                  ],
                },
              ]}
            >
              {prediction}
            </Animated.Text>
          </View>
        ) : null}</View>

        <TouchableOpacity
          style={[styles.button, fortuneCooldownLabel !== '' && styles.secondaryButtonDisabled]}
          onPress={revealPrediction}
          disabled={fortuneCooldownLabel !== ''}
        >
          <Text style={styles.buttonText}>Daily Fortune</Text>
        </TouchableOpacity>

        {fortuneCooldownLabel !== '' && <Text style={styles.cooldownText}>New Daily Fortune Available Tomorrow</Text>}

        {!fortuneRevealed && <Text style={styles.predictionPrompt}>Ask the psychic to reveal your future.</Text>}

        <TouchableOpacity
          style={[styles.secondaryButton, angelCooldownLabel !== '' && styles.secondaryButtonDisabled]}
          onPress={revealAngelNumber}
          disabled={angelCooldownLabel !== ''}
        >
          <Text style={styles.secondaryButtonText}>Reveal Angel Number</Text>
        </TouchableOpacity>

        {angelCooldownLabel !== '' && <Text style={styles.cooldownText}>New Angel Number Available Tomorrow</Text>}

        {angelNumber !== '' && (
          <View style={styles.angelNumberArea}>
            <Text style={styles.angelNumberLabel}>Lucky Angel Number</Text>
            <Text style={styles.angelNumberValue}>{angelNumber}</Text>
            <Text style={styles.angelNumberMeaning}>{angelNumberMeaning}</Text>
          </View>
        )}

        <View style={styles.numerologySection}>
          <Text style={styles.sectionHeading}>Numerology Reading - Free</Text>
          <Text style={styles.pastLifeText}>Enter your name and birthdate for a quick numerology insight.</Text>
          <TextInput
            style={styles.input}
            placeholder="Full name"
            placeholderTextColor="#999"
            value={numerologyName}
            onChangeText={setNumerologyName}
          />
          <TextInput
            style={styles.input}
            placeholder="Birthdate (MM/DD/YYYY)"
            placeholderTextColor="#999"
            value={numerologyBirthdate}
            onChangeText={setNumerologyBirthdate}
          />
          <TouchableOpacity style={styles.purchaseButton} onPress={calculateNumerology}>
            <Text style={styles.purchaseButtonText}>Calculate Free Numerology</Text>
          </TouchableOpacity>
          {numerologyResult !== '' && <Text style={styles.numerologyResult}>{numerologyResult}</Text>}
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>Choose a Door</Text>
          <Text style={styles.pastLifeText}>Open one door to reveal your message.</Text>
          <View style={styles.doorGrid}>
            {doorOptions.map((door) => (
              <View key={door.key} style={styles.doorCard}>
                <TouchableOpacity style={styles.doorCardTap} onPress={() => chooseDoor(door)}>
                  <View style={styles.doorImageFrame}>
                    <Image
                      source={door.image}
                      style={styles.doorImage}
                      resizeMode="contain"
                    />
                  </View>
                  <Text style={styles.doorTitle}>{door.label}</Text>
                </TouchableOpacity>
                {revealedDoors[door.key] && <Text style={styles.doorMessage}>{door.reveal}</Text>}
              </View>
            ))}
          </View>
        </View>

        <View style={styles.tarotSection}>
          <Text style={styles.sectionHeading}>3-Card Tarot Spread - $3.00</Text>
          <Text style={styles.pastLifeText}>Focus on one question, then buy this spread to reveal three unique tarot cards and their meanings.</Text>
          <TextInput
            style={styles.input}
            placeholder="What do you want an answer for?"
            placeholderTextColor="#999"
            value={tarotQuestion}
            onChangeText={setTarotQuestion}
          />
          <TouchableOpacity style={styles.purchaseButton} onPress={purchaseTarotSpread}>
            <Text style={styles.purchaseButtonText}>Reveal My Cards</Text>
          </TouchableOpacity>
          {tarotStatus !== '' && <Text style={styles.purchaseStatus}>{tarotStatus}</Text>}
          <View style={styles.tarotCardsContainer}>
            {tarotSpread.map((card, index) => (
              <View key={`${card.name}-${index}`} style={styles.tarotCard}>
                <View style={styles.tarotCardFace}>
                  {card.image ? (
                    <Image source={card.image} style={styles.tarotCardImage} resizeMode="contain" />
                  ) : (
                    <Text style={styles.tarotCardSymbol}>{card.symbol}</Text>
                  )}
                </View>
                <View style={styles.tarotCardTextArea}>
                  <Text style={styles.tarotCardTitle}>{card.name}</Text>
                  <Text style={styles.tarotCardText}>{card.description}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.tarotSection}>
          <Text style={styles.sectionHeading}>One Question Yes or No Pull - $1.00</Text>
          <Text style={styles.pastLifeText}>Ask one yes or no question and draw one card for a direct answer with context.</Text>
          <TextInput
            style={styles.input}
            placeholder="Ask your yes/no question"
            placeholderTextColor="#999"
            value={yesNoQuestion}
            onChangeText={setYesNoQuestion}
          />
          <TouchableOpacity style={styles.purchaseButton} onPress={purchaseYesNoPull}>
            <Text style={styles.purchaseButtonText}>Reveal My Cards</Text>
          </TouchableOpacity>
          {yesNoStatus !== '' && <Text style={styles.purchaseStatus}>{yesNoStatus}</Text>}

          {yesNoCard && (
            <View style={styles.singleCardResult}>
              <Text style={styles.singleCardAnswerLabel}>Answer: {yesNoAnswer}</Text>
              <View style={styles.singleCardFace}>
                <Image source={yesNoCard.image} style={styles.tarotCardImage} resizeMode="contain" />
              </View>
              <Text style={styles.tarotCardTitle}>{yesNoCard.name}</Text>
              <Text style={styles.tarotCardText}>{yesNoCard.description}</Text>
            </View>
          )}
        </View>

        <View style={styles.tarotSection}>
          <Text style={styles.sectionHeading}>Advanced Love Life Tarot Spread - $11.99</Text>
          <Text style={styles.pastLifeText}>Ask about an existing person of interest or whether someone new is coming soon.</Text>
          <View style={styles.toggleRow}>
            <TouchableOpacity
              style={[styles.toggleButton, loveFocus === 'existing' && styles.toggleButtonActive]}
              onPress={() => setLoveFocus('existing')}
            >
              <Text style={[styles.toggleButtonText, loveFocus === 'existing' && styles.toggleButtonTextActive]}>Existing Person</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toggleButton, loveFocus === 'incoming' && styles.toggleButtonActive]}
              onPress={() => setLoveFocus('incoming')}
            >
              <Text style={[styles.toggleButtonText, loveFocus === 'incoming' && styles.toggleButtonTextActive]}>Someone New</Text>
            </TouchableOpacity>
          </View>
          {loveFocus === 'existing' && (
            <TextInput
              style={styles.input}
              placeholder="Person of interest name or initials"
              placeholderTextColor="#999"
              value={lovePersonName}
              onChangeText={setLovePersonName}
            />
          )}
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="What do you want to know about your love life?"
            placeholderTextColor="#999"
            value={loveQuestion}
            onChangeText={setLoveQuestion}
            multiline
          />
          <TouchableOpacity style={styles.purchaseButton} onPress={purchaseLoveSpread}>
            <Text style={styles.purchaseButtonText}>Reveal My Cards</Text>
          </TouchableOpacity>
          {loveStatus !== '' && <Text style={styles.purchaseStatus}>{loveStatus}</Text>}

          <View style={styles.loveSpreadContainer}>
            {loveSpread.map((card, index) => (
              <View key={`${card.name}-${index}`} style={styles.loveTarotCard}>
                <Text style={styles.lovePositionLabel}>{card.position}</Text>
                <View style={styles.loveCardFace}>
                  <Image source={card.image} style={styles.tarotCardImage} resizeMode="contain" />
                </View>
                <Text style={styles.tarotCardTitle}>{card.name}</Text>
                <Text style={styles.tarotCardText}>{card.description}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.birthChartSection}>
          <Text style={styles.sectionHeading}>Birth Chart Readings</Text>
          <View style={styles.birthChartItem}>
            <Text style={styles.birthChartTitle}>Discover Hidden Talents and Life Purpose - $19.99</Text>
            <Text style={styles.birthChartText}>Reveal the gifts, passions and purpose already written into your birth chart.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('birth-talents')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.birthChartItem}>
            <Text style={styles.birthChartTitle}>Love Life Predictions - $11</Text>
            <Text style={styles.birthChartText}>Explore how your natal chart impacts romance, soulmate timing and emotional alignment.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('birth-love')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.birthChartItem}>
            <Text style={styles.birthChartTitle}>Career Path Predictions - $11</Text>
            <Text style={styles.birthChartText}>Discover strengths, opportunities and the career energy guiding your highest purpose.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('birth-career')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.birthChartItem}>
            <Text style={styles.birthChartTitle}>Fertility - $11</Text>
            <Text style={styles.birthChartText}>Receive gentle insight into your fertility cycles and sacred timing from your chart.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('birth-fertility')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.birthChartItem}>
            <Text style={styles.birthChartTitle}>The Waiting Room - $29.99</Text>
            <Text style={styles.birthChartText}>Explores the emotional and spiritual season surrounding your family journey.</Text>
            <Text style={styles.birthChartText}>- What feels paused</Text>
            <Text style={styles.birthChartText}>- What is developing beneath the surface</Text>
            <Text style={styles.birthChartText}>- What needs compassion</Text>
            <Text style={styles.birthChartText}>- What the waiting period may be teaching</Text>
            <Text style={styles.birthChartText}>- How to create a more peaceful inner environment</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('fertility-waiting-room')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.birthChartItem}>
            <Text style={styles.birthChartTitle}>The Parent You Are Becoming - $29.99</Text>
            <Text style={styles.birthChartText}>Explores:</Text>
            <Text style={styles.birthChartText}>- Your emerging parenting identity</Text>
            <Text style={styles.birthChartText}>- Natural gifts</Text>
            <Text style={styles.birthChartText}>- Fears or inherited patterns</Text>
            <Text style={styles.birthChartText}>- What they want to do differently</Text>
            <Text style={styles.birthChartText}>- The emotional foundation they are creating</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('fertility-parent-becoming')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.birthChartItem}>
            <Text style={styles.birthChartTitle}>The Welcome Map - $34.99</Text>
            <Text style={styles.birthChartText}>A reflective reading about creating an emotionally and spiritually welcoming next chapter:</Text>
            <Text style={styles.birthChartText}>- What is already prepared</Text>
            <Text style={styles.birthChartText}>- What still needs support</Text>
            <Text style={styles.birthChartText}>- What should be released</Text>
            <Text style={styles.birthChartText}>- What deserves nurturing</Text>
            <Text style={styles.birthChartText}>- A personalized intention or ritual</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('fertility-welcome-map')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          {readingPurchaseStatus !== '' && selectedReading.startsWith('birth') && <Text style={styles.purchaseStatus}>{readingPurchaseStatus}</Text>}
        </View>

        <View style={styles.spiritBabySection}>
          <Text style={styles.sectionHeading}>Spirit Baby Readings</Text>
          <View style={styles.spiritBabyItem}>
            <Text style={styles.spiritBabyTitle}>Signs from Your Spirit Baby - $11</Text>
            <Text style={styles.spiritBabyText}>Learn to recognize messages, dreams and subtle signs from your spirit child.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('spirit-signs')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.spiritBabyItem}>
            <Text style={styles.spiritBabyTitle}>Gender of Your Spirit Baby - $11</Text>
            <Text style={styles.spiritBabyText}>Tune into the gentle energy and intuition around your spirit baby’s gender essence.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('spirit-gender')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.spiritBabyItem}>
            <Text style={styles.spiritBabyTitle}>The Soul&apos;s Introduction - $24.99</Text>
            <Text style={styles.spiritBabyText}>A symbolic spirit-baby portrait containing:</Text>
            <Text style={styles.spiritBabyText}>- A central personality archetype</Text>
            <Text style={styles.spiritBabyText}>- A color</Text>
            <Text style={styles.spiritBabyText}>- A celestial symbol</Text>
            <Text style={styles.spiritBabyText}>- A possible emotional quality</Text>
            <Text style={styles.spiritBabyText}>- A message for the parent</Text>
            <Text style={styles.spiritBabyText}>- The type of family connection represented</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('spirit-souls-introduction')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.spiritBabyItem}>
            <Text style={styles.spiritBabyTitle}>The Family Constellation - $39.99</Text>
            <Text style={styles.spiritBabyText}>Explores the energetic roles within a future or expanding family:</Text>
            <Text style={styles.spiritBabyText}>- Your parenting archetype</Text>
            <Text style={styles.spiritBabyText}>- The partner&apos;s role</Text>
            <Text style={styles.spiritBabyText}>- The energy associated with a future child</Text>
            <Text style={styles.spiritBabyText}>- Family strengths</Text>
            <Text style={styles.spiritBabyText}>- Patterns the family may be called to heal</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('spirit-family-constellation')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.spiritBabyItem}>
            <Text style={styles.spiritBabyTitle}>The Dream Nursery - $19.99</Text>
            <Text style={styles.spiritBabyText}>If you are experiencing dreams involving babies, pregnancy, children, names, animals or repeating symbols.</Text>
            <Text style={styles.spiritBabyText}>The reading could interpret:</Text>
            <Text style={styles.spiritBabyText}>- The dream&apos;s emotional meaning</Text>
            <Text style={styles.spiritBabyText}>- Whether it reflects hope, fear, grief or intuition</Text>
            <Text style={styles.spiritBabyText}>- Important symbols</Text>
            <Text style={styles.spiritBabyText}>- The message the subconscious may be communicating</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseReading('spirit-dream-nursery')}>
              <Text style={styles.buyNowButtonText}>Receive My Messages</Text>
            </TouchableOpacity>
          </View>
          {readingPurchaseStatus !== '' && selectedReading.startsWith('spirit') && <Text style={styles.purchaseStatus}>{readingPurchaseStatus}</Text>}
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>Delivery Email For Paid Readings</Text>
          <Text style={styles.pastLifeText}>Paid readings are delivered by email within 24-72 hours. Enter one email here before purchasing.</Text>
          <TextInput
            style={styles.input}
            placeholder="Delivery email for paid readings"
            placeholderTextColor="#999"
            value={customerEmail}
            onChangeText={setCustomerEmail}
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>Choose a Room - $9.99</Text>
          <Text style={styles.pastLifeText}>Pick the room that calls to you. Its hidden life theme appears after selection.</Text>
          <View style={styles.roomGrid}>
            {Object.keys(roomThemes).map((roomName) => (
              <TouchableOpacity
                key={roomName}
                style={[styles.roomButton, roomSelection === roomName && styles.toggleButtonActive]}
                onPress={() => chooseRoom(roomName)}
              >
                <Text style={[styles.toggleButtonText, roomSelection === roomName && styles.toggleButtonTextActive]}>{roomName}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {roomReveal !== '' && <Text style={styles.pastLifeText}>{roomReveal}</Text>}
          <TouchableOpacity style={styles.purchaseButton} onPress={purchaseRoomReading}>
            <Text style={styles.purchaseButtonText}>Unlock My Fate</Text>
          </TouchableOpacity>
          {roomStatus !== '' && <Text style={styles.purchaseStatus}>{roomStatus}</Text>}
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>The Sealed Envelope</Text>
          <Text style={styles.pastLifeText}>A prediction remains locked until your selected moment.</Text>
          <View style={styles.roomGrid}>
            <TouchableOpacity
              style={[styles.roomButton, sealedOption === 'sealed-envelope-30-days' && styles.toggleButtonActive]}
              onPress={() => setSealedOption('sealed-envelope-30-days')}
            >
              <Text style={[styles.toggleButtonText, sealedOption === 'sealed-envelope-30-days' && styles.toggleButtonTextActive]}>Open in 30 days - $14.99</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.roomButton, sealedOption === 'sealed-envelope-birthday' && styles.toggleButtonActive]}
              onPress={() => setSealedOption('sealed-envelope-birthday')}
            >
              <Text style={[styles.toggleButtonText, sealedOption === 'sealed-envelope-birthday' && styles.toggleButtonTextActive]}>Open on birthday - $19.99</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.roomButton, sealedOption === 'sealed-envelope-new-year' && styles.toggleButtonActive]}
              onPress={() => setSealedOption('sealed-envelope-new-year')}
            >
              <Text style={[styles.toggleButtonText, sealedOption === 'sealed-envelope-new-year' && styles.toggleButtonTextActive]}>Open at new year - $19.99</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.roomButton, sealedOption === 'sealed-envelope-major-decision' && styles.toggleButtonActive]}
              onPress={() => setSealedOption('sealed-envelope-major-decision')}
            >
              <Text style={[styles.toggleButtonText, sealedOption === 'sealed-envelope-major-decision' && styles.toggleButtonTextActive]}>Open after major decision - $24.99</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity style={styles.purchaseButton} onPress={purchaseSealedEnvelope}>
            <Text style={styles.purchaseButtonText}>Unlock My Fate</Text>
          </TouchableOpacity>
          {sealedStatus !== '' && <Text style={styles.purchaseStatus}>{sealedStatus}</Text>}
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>The Prediction House Readings</Text>
          <Text style={styles.pastLifeText}>Story-style tarot journeys for clarity, timing and life direction.</Text>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Prediction House Grand Tour - $59.99</Text>
            <Text style={styles.spellAddOnText}>A complete reading through seven symbolic rooms: current energy, love, career, family, past influences, hidden truth and future direction.</Text>
            <Text style={styles.spellAddOnText}>1. The Foyer: Where you stand now</Text>
            <Text style={styles.spellAddOnText}>2. The Parlor: Love and relationships</Text>
            <Text style={styles.spellAddOnText}>3. The Study: Career, money and purpose</Text>
            <Text style={styles.spellAddOnText}>4. The Kitchen: Emotional nourishment and home life</Text>
            <Text style={styles.spellAddOnText}>5. The Attic: The past that still follows you</Text>
            <Text style={styles.spellAddOnText}>6. The Locked Room: What you are not seeing</Text>
            <Text style={styles.spellAddOnText}>7. The Rooftop: What may be coming next</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-grand-tour')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Locked Room - $19.99</Text>
            <Text style={styles.spellAddOnText}>Reveals the issue, feeling or possibility you may be avoiding or unable to see clearly.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-locked-room')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>Two Doors - $24.99</Text>
            <Text style={styles.spellAddOnText}>Compares two possible choices: what each path offers, costs, delays and awakens.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-two-doors')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>Firsts, Lasts &amp; Returns - $24.99</Text>
            <Text style={styles.spellAddOnText}>Reveals:</Text>
            <Text style={styles.spellAddOnText}>- The next meaningful beginning</Text>
            <Text style={styles.spellAddOnText}>- Something approaching completion</Text>
            <Text style={styles.spellAddOnText}>- Something or someone likely to return</Text>
            <Text style={styles.spellAddOnText}>- An unexpected development</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-firsts-lasts-returns')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>After the Wish Comes True - $24.99</Text>
            <Text style={styles.spellAddOnText}>Instead of asking whether a goal will happen, this reading explores:</Text>
            <Text style={styles.spellAddOnText}>- What life might look like afterward</Text>
            <Text style={styles.spellAddOnText}>- Whether you are prepared for it</Text>
            <Text style={styles.spellAddOnText}>- What the achievement may require</Text>
            <Text style={styles.spellAddOnText}>- The next challenge that follows</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-after-the-wish')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Guest at the Door - $19.99</Text>
            <Text style={styles.spellAddOnText}>Explores the person, opportunity, lesson or change approaching your life.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-guest-at-door')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Attic - $29.99</Text>
            <Text style={styles.spellAddOnText}>Examines an old story, inherited pattern or unresolved experience still influencing the present.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-attic')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Renovation Reading - $29.99</Text>
            <Text style={styles.spellAddOnText}>Identifies what to preserve, repair, release and rebuild in your life.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-renovation-reading')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Rooftop Forecast - $39.99</Text>
            <Text style={styles.spellAddOnText}>A bird&apos;s-eye view of the next six months, including turning points, opportunities and cautions.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-rooftop-forecast')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Room Calling You - $14.99</Text>
            <Text style={styles.spellAddOnText}>Reveals which part of life most needs attention now and what it is asking you to do.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-room-calling-you')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The House After Midnight - $34.99</Text>
            <Text style={styles.spellAddOnText}>A deeper reading of fears, dreams, intuition, hidden desires and subconscious messages.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-after-midnight')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The New Key</Text>
            <Text style={styles.spellAddOnText}>Shows what will unlock your next chapter and what has kept the door closed.</Text>
            <TouchableOpacity
              style={styles.buyNowButton}
              onPress={() => setHouseReadingStatus('Please inquire by email to book The New Key reading.')}
            >
              <Text style={styles.buyNowButtonText}>Inquire Here</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Future Memory - $29.99</Text>
            <Text style={styles.spellAddOnText}>A detailed intuitive portrait of one potentially meaningful future moment:</Text>
            <Text style={styles.spellAddOnText}>- Where you may be</Text>
            <Text style={styles.spellAddOnText}>- What has changed</Text>
            <Text style={styles.spellAddOnText}>- Who or what surrounds them</Text>
            <Text style={styles.spellAddOnText}>- How they may feel</Text>
            <Text style={styles.spellAddOnText}>- What choices could help lead there</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-future-memory')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>Your Next Plot Twist - $17.99</Text>
            <Text style={styles.spellAddOnText}>A short reading about:</Text>
            <Text style={styles.spellAddOnText}>- What may change unexpectedly</Text>
            <Text style={styles.spellAddOnText}>- What initially appears disruptive</Text>
            <Text style={styles.spellAddOnText}>- The hidden opportunity</Text>
            <Text style={styles.spellAddOnText}>- How to respond</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-next-plot-twist')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Year You Are Becoming - $39.99</Text>
            <Text style={styles.spellAddOnText}>- The identity you are growing into</Text>
            <Text style={styles.spellAddOnText}>- Qualities being strengthened</Text>
            <Text style={styles.spellAddOnText}>- Patterns being outgrown</Text>
            <Text style={styles.spellAddOnText}>- The decision that defines the next chapter</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-year-you-are-becoming')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Unasked Question - $14.99</Text>
            <Text style={styles.spellAddOnText}>The reader identifies the deeper question underneath your stated concern and gives guidance on what deserves attention.</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-unasked-question')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.sectionHeading}>Personal Pattern Readings</Text>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Pattern Breaker - $29.99</Text>
            <Text style={styles.spellAddOnText}>Identifies:</Text>
            <Text style={styles.spellAddOnText}>- The repeating cycle</Text>
            <Text style={styles.spellAddOnText}>- What triggers it</Text>
            <Text style={styles.spellAddOnText}>- Why it has been difficult to leave</Text>
            <Text style={styles.spellAddOnText}>- What keeps rewarding the pattern</Text>
            <Text style={styles.spellAddOnText}>- The action that begins breaking it</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-pattern-breaker')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Energy Budget - $24.99</Text>
            <Text style={styles.spellAddOnText}>Treats emotional energy like money:</Text>
            <Text style={styles.spellAddOnText}>- Where it is being overspent</Text>
            <Text style={styles.spellAddOnText}>- What produces little return</Text>
            <Text style={styles.spellAddOnText}>- What deserves greater investment</Text>
            <Text style={styles.spellAddOnText}>- Where energy can be restored</Text>
            <Text style={styles.spellAddOnText}>- The most valuable priority</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-energy-budget')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>The Invisible Agreement - $29.99</Text>
            <Text style={styles.spellAddOnText}>Explores unspoken expectations in existing family dynamics, including in-laws, a spouse, siblings or close relatives:</Text>
            <Text style={styles.spellAddOnText}>- What you believe you must provide</Text>
            <Text style={styles.spellAddOnText}>- What others may expect</Text>
            <Text style={styles.spellAddOnText}>- Where resentment develops</Text>
            <Text style={styles.spellAddOnText}>- What needs to be renegotiated</Text>
            <TouchableOpacity style={styles.buyNowButton} onPress={() => purchaseHouseReading('house-invisible-agreement')}>
              <Text style={styles.buyNowButtonText}>Unlock My Fate</Text>
            </TouchableOpacity>
          </View>

          {houseReadingStatus !== '' && <Text style={styles.purchaseStatus}>{houseReadingStatus}</Text>}
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>Spells</Text>
          <Text style={styles.pastLifeText}>Order custom spell work for love, abundance or curse removal.</Text>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>Core Rituals</Text>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Personalized Intention Candle - $12.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Personalized Intention Candle')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Confidence and Empowerment - $17.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Confidence and Empowerment')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Self-Love - $19.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Self-Love')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Energy Cleansing - $19.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Energy Cleansing')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Peaceful Sleep - $19.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Peaceful Sleep')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>Advanced Rituals</Text>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Protection - $24.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Protection')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Abundance and Prosperity - $29.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Abundance and Prosperity')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Career Opportunity - $29.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Career Opportunity')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Road-Opening - $34.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Road-Opening')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Home Cleansing and Protection - $34.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Home Cleansing and Protection')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Motivation and Success - $29.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Motivation and Success')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Heart Healing - $29.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Heart Healing')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Relationship Harmony - $34.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Relationship Harmony')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>Premium Rituals</Text>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Seven-Day Candle - $49.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Seven-Day Candle')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Personalized Multi-Candle - $59.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Personalized Multi-Candle')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>Full Moon Manifestation - $44.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('Full Moon Manifestation')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.spellRitualRow}>
              <Text style={styles.spellRitualText}>New Moon Road-Opening - $44.99</Text>
              <TouchableOpacity style={styles.spellRitualButton} onPress={() => orderSpellRitual('New Moon Road-Opening')}>
                <Text style={styles.spellRitualButtonText}>Begin My Ritual</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.spellOptionsSection}>
            <Text style={styles.readingDetailTitle}>Other Spell Requests - $36.99</Text>
            <View style={styles.spellOptionsGrid}>
              <TouchableOpacity
                style={[styles.spellOptionButton, spellType === 'custom' && styles.toggleButtonActive]}
                onPress={() => setSpellType('custom')}
              >
                <Text style={[styles.toggleButtonText, spellType === 'custom' && styles.toggleButtonTextActive]}>Activate My Spell</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.spellOptionButton, spellType === 'curse-removal' && styles.toggleButtonActive]}
                onPress={() => setSpellType('curse-removal')}
              >
                <Text style={[styles.toggleButtonText, spellType === 'curse-removal' && styles.toggleButtonTextActive]}>Activate My Spell</Text>
              </TouchableOpacity>
            </View>
          </View>

          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="Share your intention or details for this spell request"
            placeholderTextColor="#999"
            value={spellRequest}
            onChangeText={setSpellRequest}
            multiline
          />

          <TextInput
            style={styles.input}
            placeholder="Email for completed spell updates"
            placeholderTextColor="#999"
            value={spellEmail}
            onChangeText={setSpellEmail}
            keyboardType="email-address"
            autoCapitalize="none"
          />

          <View style={styles.spellAddOnCard}>
            <Text style={styles.readingDetailTitle}>Spell Add-Ons</Text>
            <Text style={styles.spellAddOnText}>Tarot card pulled after spell - $4.99</Text>
            <Text style={styles.spellAddOnText}>Personalized written affirmation - $3.99</Text>
            <Text style={styles.spellAddOnText}>Photograph - $1.00</Text>
            <Text style={styles.spellAddOnText}>Short video - $9.99</Text>
            <Text style={styles.spellAddOnText}>24-hour priority service - $12.99</Text>
          </View>

          <TouchableOpacity style={styles.purchaseButton} onPress={placeSpellOrder}>
            <Text style={styles.purchaseButtonText}>Activate My Spell</Text>
          </TouchableOpacity>
          {spellStatus !== '' && <Text style={styles.purchaseStatus}>{spellStatus}</Text>}
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>Synastry Love Reading - $19.99</Text>
          <Text style={styles.pastLifeText}>Provide both birth charts for a deep compatibility reading between you and your partner or person of interest.</Text>
          <Text style={styles.synastryHighlights}>Includes insight on compatibility, communication and long-term potential.</Text>

          <Text style={styles.synastrySubheading}>Your Birth Chart</Text>
          <TextInput
            style={styles.input}
            placeholder="Your full name"
            placeholderTextColor="#999"
            value={synastryYourName}
            onChangeText={setSynastryYourName}
          />
          <TextInput
            style={styles.input}
            placeholder="Your birthdate (MM/DD/YYYY)"
            placeholderTextColor="#999"
            value={synastryYourBirthdate}
            onChangeText={setSynastryYourBirthdate}
          />
          <TextInput
            style={styles.input}
            placeholder="Your birth time (optional)"
            placeholderTextColor="#999"
            value={synastryYourBirthtime}
            onChangeText={setSynastryYourBirthtime}
          />
          <TextInput
            style={styles.input}
            placeholder="Your birth place (city, state/country)"
            placeholderTextColor="#999"
            value={synastryYourBirthplace}
            onChangeText={setSynastryYourBirthplace}
          />

          <Text style={styles.synastrySubheading}>Partner Birth Chart</Text>
          <TextInput
            style={styles.input}
            placeholder="Partner full name"
            placeholderTextColor="#999"
            value={synastryPartnerName}
            onChangeText={setSynastryPartnerName}
          />
          <TextInput
            style={styles.input}
            placeholder="Partner birthdate (MM/DD/YYYY)"
            placeholderTextColor="#999"
            value={synastryPartnerBirthdate}
            onChangeText={setSynastryPartnerBirthdate}
          />
          <TextInput
            style={styles.input}
            placeholder="Partner birth time (optional)"
            placeholderTextColor="#999"
            value={synastryPartnerBirthtime}
            onChangeText={setSynastryPartnerBirthtime}
          />
          <TextInput
            style={styles.input}
            placeholder="Partner birth place (city, state/country)"
            placeholderTextColor="#999"
            value={synastryPartnerBirthplace}
            onChangeText={setSynastryPartnerBirthplace}
          />

          <TouchableOpacity style={styles.purchaseButton} onPress={purchaseSynastryReading}>
            <Text style={styles.purchaseButtonText}>Purchase Synastry Love Reading for $19.99</Text>
          </TouchableOpacity>
          {synastryStatus !== '' && <Text style={styles.purchaseStatus}>{synastryStatus}</Text>}
        </View>

        <View style={styles.pastLifeSection}>
          <Text style={styles.sectionHeading}>Past Life Reading - $19.99</Text>
          <Text style={styles.pastLifeText}>Answer a few questions and purchase this on-demand past life reading.</Text>
          <TextInput
            style={styles.input}
            placeholder="Full name"
            placeholderTextColor="#999"
            value={pastLifeName}
            onChangeText={setPastLifeName}
          />
          <TextInput
            style={styles.input}
            placeholder="Birthdate (MM/DD/YYYY)"
            placeholderTextColor="#999"
            value={pastLifeBirthdate}
            onChangeText={setPastLifeBirthdate}
          />
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="What past life theme or memory feels important?"
            placeholderTextColor="#999"
            value={pastLifeMemory}
            onChangeText={setPastLifeMemory}
            multiline
          />
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="Do you have any major birthmarks or large scars?"
            placeholderTextColor="#999"
            value={pastLifeBirthmark}
            onChangeText={setPastLifeBirthmark}
            multiline
          />
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="Are you drawn to a certain place or called to travel somewhere?"
            placeholderTextColor="#999"
            value={pastLifeTravelCall}
            onChangeText={setPastLifeTravelCall}
            multiline
          />
          <TouchableOpacity style={styles.purchaseButton} onPress={purchasePastLifeReading}>
            <Text style={styles.purchaseButtonText}>Show Me My Past Life</Text>
          </TouchableOpacity>
          {pastLifeStatus !== '' && <Text style={styles.purchaseStatus}>{pastLifeStatus}</Text>}
        </View>

        <View style={styles.historySection}>
          <Text style={styles.historyTitle}>Recent Predictions</Text>
          {history.length === 0 ? (
            <Text style={styles.historyEmpty}>No predictions yet. Tap the button above.</Text>
          ) : (
            history.map((item, index) => (
              <View key={`${item}-${index}`} style={styles.historyItem}>
                <Text style={styles.historyText}>{item}</Text>
              </View>
            ))
          )}
        </View>

        <View style={styles.supportBox}>
          <Text style={styles.supportTitle}>Need Help with a Reading?</Text>
          <Text style={styles.supportText}>Please email the psychic directly at thepredictionhouse@gmail.com</Text>
        </View>

        <TouchableOpacity style={styles.clearButton} onPress={clearHistory}>
          <Text style={styles.clearButtonText}>Clear History</Text>
        </TouchableOpacity>

        <Text style={styles.footerNote}>For entertainment purposes only. No results are guaranteed.</Text>
        <Text style={styles.footer}>Built with Expo for Android and iOS.</Text>
      </ScrollView>
      <StatusBar style="auto" />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#000000',
  },
  container: {
    flexGrow: 1,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  header: {
    width: '100%',
    marginBottom: 20,
    alignItems: 'center',
  },
  logo: {
    width: 520,
    height: 320,
    resizeMode: 'contain',
    marginBottom: 16,
    backgroundColor: 'transparent',
  },
  logoTagline: {
    color: '#e7ddc8',
    fontSize: 18,
    lineHeight: 26,
    textAlign: 'center',
    fontFamily: 'PlayfairDisplay_400Regular',
    marginTop: -2,
    marginBottom: 8,
    paddingHorizontal: 10,
  },
  title: {
    fontSize: 34,
    fontWeight: '700',
    color: '#f2f2f2',
    textAlign: 'center',
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  subtitle: {
    fontSize: 16,
    color: '#d1d1d1',
    textAlign: 'center',
    marginTop: 10,
    lineHeight: 24,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  card: {
    backgroundColor: '#111111',
    borderRadius: 28,
    borderWidth: 1,
    borderColor: '#333333',
    padding: 28,
    marginBottom: 24,
    width: '100%',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.18,
    shadowRadius: 18,
  },
  prediction: {
    fontSize: 32,
    color: '#f4d58a',
    lineHeight: 44,
    textAlign: 'center',
    fontFamily: 'PlayfairDisplay_700Bold',
    textShadowColor: 'rgba(244, 213, 138, 0.85)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 14,
  },
  predictionInner: {
    width: '100%',
    minHeight: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  predictionStars: {
    position: 'absolute',
    top: 16,
    left: 16,
    right: 16,
    bottom: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  predictionStar: {
    color: '#f7f7f7',
    textShadowColor: 'rgba(255,255,255,0.9)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  predictionStarTiny: {
    fontSize: 16,
    opacity: 0.45,
  },
  predictionStarSmall: {
    fontSize: 20,
    opacity: 0.55,
  },
  predictionStarMedium: {
    fontSize: 24,
    opacity: 0.65,
  },
  predictionPrompt: {
    color: '#9f9f9f',
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 18,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  cooldownText: {
    color: '#b7a97a',
    fontSize: 13,
    textAlign: 'center',
    marginTop: -8,
    marginBottom: 14,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  button: {
    backgroundColor: '#222228',
    borderRadius: 20,
    paddingVertical: 18,
    paddingHorizontal: 32,
    marginBottom: 20,
    width: '100%',
    alignItems: 'center',
  },
  buttonText: {
    color: '#f8f8f8',
    fontSize: 18,
    fontWeight: '800',
    fontFamily: 'serif',
  },
  purchaseSection: {
    backgroundColor: '#111111',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 22,
    width: '100%',
    marginBottom: 24,
  },
  purchaseTitle: {
    color: '#f5f5f5',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 10,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  purchaseText: {
    color: '#b8b8b8',
    fontSize: 15,
    lineHeight: 24,
    marginBottom: 18,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  pastLifeSection: {
    backgroundColor: '#111111',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 22,
    width: '100%',
    marginBottom: 24,
  },
  numerologySection: {
    backgroundColor: '#111111',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 22,
    width: '100%',
    marginBottom: 24,
  },
  pastLifeText: {
    color: '#d1d1d1',
    fontSize: 15,
    lineHeight: 24,
    marginBottom: 16,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  input: {
    width: '100%',
    backgroundColor: '#1f1f26',
    borderRadius: 18,
    borderColor: '#2f2f33',
    borderWidth: 1,
    color: '#f5f5f5',
    fontSize: 16,
    padding: 14,
    marginBottom: 14,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  numerologyResult: {
    color: '#f5f5f5',
    fontSize: 16,
    lineHeight: 24,
    marginTop: 12,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  footerNote: {
    color: '#b8b0a0',
    fontSize: 13,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  purchaseButton: {
    backgroundColor: '#2a2a2f',
    borderRadius: 20,
    paddingVertical: 16,
    paddingHorizontal: 32,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  purchaseButtonText: {
    color: '#f8f8f8',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  buyNowButton: {
    backgroundColor: '#1d1d22',
    borderRadius: 18,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignSelf: 'flex-start',
    marginTop: 12,
  },
  buyNowButtonText: {
    color: '#fcebb5',
    fontSize: 14,
    fontWeight: '700',
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  readingDetailCard: {
    backgroundColor: '#14141b',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 20,
    marginTop: 20,
  },
  readingDetailTitle: {
    color: '#f8f8f8',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 10,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  readingDetailText: {
    color: '#c7c3b9',
    fontSize: 15,
    lineHeight: 24,
    marginBottom: 16,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  tarotSection: {
    backgroundColor: '#111111',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 22,
    width: '100%',
    marginBottom: 24,
  },
  tarotCardsContainer: {
    width: '100%',
    marginTop: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
  },
  tarotCard: {
    backgroundColor: '#1b1b21',
    borderRadius: 28,
    borderWidth: 1,
    borderColor: '#3d324a',
    padding: 12,
    marginBottom: 14,
    shadowColor: 'rgba(0,0,0,0.5)',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.15,
    shadowRadius: 18,
    width: '32%',
  },
  tarotCardFace: {
    width: '100%',
    height: 220,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#7b6a99',
    backgroundColor: '#191623',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    padding: 8,
    overflow: 'hidden',
  },
  tarotCardImage: {
    width: '100%',
    height: '100%',
    borderRadius: 22,
  },
  tarotCardSymbol: {
    fontSize: 68,
    color: '#f7e3a1',
    marginBottom: 12,
  },
  tarotCardTitle: {
    color: '#f8f8f8',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  tarotCardText: {
    color: '#c7c3b9',
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  singleCardResult: {
    width: '100%',
    backgroundColor: '#1b1b21',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#3d324a',
    padding: 14,
    marginTop: 14,
    alignItems: 'center',
  },
  singleCardAnswerLabel: {
    color: '#f4d56e',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 10,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  singleCardFace: {
    width: '100%',
    height: 250,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#7b6a99',
    backgroundColor: '#191623',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    padding: 8,
    overflow: 'hidden',
  },
  toggleRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 10,
  },
  toggleButton: {
    flex: 1,
    backgroundColor: '#1d1d22',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2f2f33',
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#2e2736',
    borderColor: '#6f5b88',
  },
  toggleButtonText: {
    color: '#d6d0c5',
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  toggleButtonTextActive: {
    color: '#f5e9c6',
  },
  spellOptionsGrid: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 10,
  },
  spellOptionButton: {
    width: '48%',
    backgroundColor: '#1d1d22',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2f2f33',
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
  },
  spellAddOnCard: {
    width: '100%',
    backgroundColor: '#14141b',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 18,
    marginBottom: 14,
  },
  spellAddOnText: {
    color: '#c7c3b9',
    fontSize: 15,
    lineHeight: 24,
    marginBottom: 6,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  spellRitualRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  spellRitualText: {
    flex: 1,
    color: '#c7c3b9',
    fontSize: 15,
    lineHeight: 24,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  spellRitualButton: {
    backgroundColor: '#1d1d22',
    borderRadius: 16,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#2f2f33',
  },
  spellRitualButtonText: {
    color: '#fcebb5',
    fontSize: 13,
    fontWeight: '700',
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  loveSpreadContainer: {
    width: '100%',
    marginTop: 14,
  },
  loveTarotCard: {
    width: '100%',
    backgroundColor: '#1b1b21',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#3d324a',
    padding: 14,
    marginBottom: 14,
  },
  lovePositionLabel: {
    color: '#f4d56e',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 10,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  loveCardFace: {
    width: '100%',
    height: 220,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#7b6a99',
    backgroundColor: '#191623',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    padding: 8,
    overflow: 'hidden',
  },
  purchaseStatus: {
    color: '#ded7ca',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: '#f5e5d0',
  },
  loadingText: {
    color: '#f2e1c4',
    fontSize: 18,
    fontFamily: 'serif',
  },
  infoCard: {
    backgroundColor: '#111111',
    borderRadius: 22,
    padding: 20,
    width: '100%',
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#2f2f33',
  },
  infoTitle: {
    color: '#f7f7f7',
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 8,
    fontFamily: 'serif',
  },
  infoText: {
    color: '#bdbdbd',
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'serif',
  },
  birthChartSection: {
    backgroundColor: '#111111',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 22,
    width: '100%',
    marginBottom: 24,
  },
  spiritBabySection: {
    backgroundColor: '#111111',
    borderRadius: 26,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 22,
    width: '100%',
    marginBottom: 24,
  },
  sectionHeading: {
    color: '#f5f5f5',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 14,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  birthChartItem: {
    marginBottom: 16,
  },
  birthChartTitle: {
    color: '#f8f8f8',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  birthChartText: {
    color: '#b8b8b8',
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  spiritBabyItem: {
    marginBottom: 16,
  },
  spiritBabyTitle: {
    color: '#f8f8f8',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  spiritBabyText: {
    color: '#b8b8b8',
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  doorGrid: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  doorCard: {
    width: '31.5%',
    backgroundColor: '#14141b',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  doorCardTap: {
    width: '100%',
    alignItems: 'center',
  },
  doorImageFrame: {
    width: '100%',
    height: 170,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#1d1d22',
    marginBottom: 10,
  },
  doorImage: {
    width: '100%',
    height: '100%',
  },
  doorTitle: {
    color: '#f8f8f8',
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 0,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  doorMessage: {
    color: '#c7c3b9',
    fontSize: 13,
    lineHeight: 19,
    marginTop: 10,
    textAlign: 'center',
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  roomGrid: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 12,
  },
  roomButton: {
    width: '48%',
    backgroundColor: '#1d1d22',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2f2f33',
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
  },
  synastrySubheading: {
    color: '#f4d56e',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
    marginTop: 4,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  synastryHighlights: {
    color: '#c7c3b9',
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 14,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  angelNumberArea: {
    width: '100%',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  angelNumberDecorations: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    width: '100%',
    marginBottom: 10,
    paddingHorizontal: 12,
  },
  angelNumberFireworks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    marginTop: 6,
    marginBottom: 6,
    width: '100%',
    paddingHorizontal: 12,
  },
  angelNumberDecoration: {
    color: '#ffffff',
    textShadowColor: 'rgba(255,255,255,0.9)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 18,
  },
  angelNumberDecorationTiny: {
    fontSize: 18,
    opacity: 0.55,
  },
  angelNumberDecorationSmall: {
    fontSize: 24,
    opacity: 0.65,
  },
  angelNumberDecorationMedium: {
    fontSize: 30,
    opacity: 0.75,
  },
  angelNumberDecorationMoon: {
    fontSize: 34,
    opacity: 0.8,
  },
  angelNumberDecorationPlanet: {
    fontSize: 22,
    opacity: 0.7,
  },
  angelNumberDecorationLarge: {
    fontSize: 40,
    opacity: 0.95,
  },
  angelNumberDecorationsBottom: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    marginTop: 6,
    marginBottom: 8,
    width: '100%',
    paddingHorizontal: 12,
  },
  angelNumberLabel: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '800',
    textAlign: 'center',
    width: '100%',
    marginBottom: 10,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  angelNumberValue: {
    color: '#f4d56e',
    fontSize: 88,
    fontWeight: '900',
    letterSpacing: 8,
    width: '100%',
    textAlign: 'center',
    fontFamily: 'PlayfairDisplay_700Bold',
    marginVertical: 14,
    textShadowColor: 'rgba(244, 213, 110, 0.75)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 18,
  },
  angelNumberMeaning: {
    color: '#ffffff',
    fontSize: 20,
    width: '100%',
    textAlign: 'center',
    marginTop: 18,
    lineHeight: 30,
    paddingHorizontal: 18,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  secondaryButton: {
    backgroundColor: '#3b3b43',
    borderRadius: 20,
    paddingVertical: 16,
    paddingHorizontal: 32,
    marginBottom: 20,
    width: '100%',
    alignItems: 'center',
  },
  secondaryButtonDisabled: {
    backgroundColor: '#2b2b32',
  },
  secondaryButtonText: {
    color: '#ece7dc',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'serif',
  },
  softGlow1: {
    position: 'absolute',
    top: -80,
    right: -40,
    width: 300,
    height: 300,
    borderRadius: 150,
    backgroundColor: 'rgba(215, 175, 130, 0.14)',
    shadowColor: '#d7b27f',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.35,
    shadowRadius: 90,
  },
  backgroundGlow: {
    position: 'absolute',
    top: -100,
    left: -90,
    width: 320,
    height: 320,
    borderRadius: 160,
    backgroundColor: 'rgba(178, 132, 82, 0.14)',
    shadowColor: '#b28452',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.35,
    shadowRadius: 90,
  },
  star1: {
    position: 'absolute',
    top: 180,
    left: '8%',
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#c8b3ff',
    opacity: 0.24,
    shadowColor: 'rgba(200, 179, 255, 0.28)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.28,
    shadowRadius: 12,
  },
  star2: {
    position: 'absolute',
    top: 200,
    left: '18%',
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#b4f1ff',
    opacity: 0.22,
    shadowColor: 'rgba(180, 241, 255, 0.26)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.26,
    shadowRadius: 12,
  },
  star3: {
    position: 'absolute',
    top: 240,
    left: '28%',
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#ffc8ff',
    opacity: 0.22,
    shadowColor: 'rgba(255, 200, 255, 0.26)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.26,
    shadowRadius: 12,
  },
  star4: {
    position: 'absolute',
    top: 310,
    left: '38%',
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#d0f7e2',
    opacity: 0.23,
    shadowColor: 'rgba(208, 247, 226, 0.28)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.28,
    shadowRadius: 12,
  },
  star5: {
    position: 'absolute',
    top: 210,
    left: '48%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#fbd4ff',
    opacity: 0.2,
    shadowColor: 'rgba(251, 212, 255, 0.25)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
  },
  star6: {
    position: 'absolute',
    top: 200,
    left: '58%',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#d3f0ff',
    opacity: 0.22,
    shadowColor: 'rgba(211, 240, 255, 0.26)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.26,
    shadowRadius: 10,
  },
  star7: {
    position: 'absolute',
    top: 220,
    left: '68%',
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  star8: {
    position: 'absolute',
    top: 260,
    left: '78%',
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  star9: {
    position: 'absolute',
    top: 230,
    left: '12%',
    width: 9,
    height: 9,
    borderRadius: 4.5,
    backgroundColor: '#c2f2ff',
    opacity: 0.18,
    shadowColor: 'rgba(194, 242, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star10: {
    position: 'absolute',
    top: 240,
    left: '24%',
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#f3c9ff',
    opacity: 0.18,
    shadowColor: 'rgba(243, 201, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star11: {
    position: 'absolute',
    top: 220,
    left: '34%',
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#e7f2ff',
    opacity: 0.14,
    shadowColor: 'rgba(231, 242, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star12: {
    position: 'absolute',
    top: 250,
    left: '52%',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#ecd7ff',
    opacity: 0.2,
    shadowColor: 'rgba(236, 215, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 10,
  },
  star13: {
    position: 'absolute',
    top: 340,
    left: '70%',
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#d0c2ff',
    opacity: 0.18,
    shadowColor: 'rgba(208, 194, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 10,
  },
  star14: {
    position: 'absolute',
    top: 280,
    left: '86%',
    width: 9,
    height: 9,
    borderRadius: 4.5,
    backgroundColor: '#d9f7ff',
    opacity: 0.18,
    shadowColor: 'rgba(217, 247, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star15: {
    position: 'absolute',
    top: 210,
    left: '74%',
    width: 11,
    height: 11,
    borderRadius: 5.5,
    backgroundColor: '#ffd4eb',
    opacity: 0.2,
    shadowColor: 'rgba(255, 212, 235, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star16: {
    position: 'absolute',
    top: 360,
    left: '5%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#d9f6dc',
    opacity: 0.16,
    shadowColor: 'rgba(217, 246, 220, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star17: {
    position: 'absolute',
    top: 220,
    left: '90%',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#e7d7ff',
    opacity: 0.18,
    shadowColor: 'rgba(231, 215, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star18: {
    position: 'absolute',
    top: 220,
    left: '46%',
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#e2f7ff',
    opacity: 0.16,
    shadowColor: 'rgba(226, 247, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star19: {
    position: 'absolute',
    top: 190,
    left: '15%',
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#f6d4ff',
    opacity: 0.16,
    shadowColor: 'rgba(246, 212, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star20: {
    position: 'absolute',
    top: 230,
    left: '72%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#cff7ff',
    opacity: 0.16,
    shadowColor: 'rgba(207, 247, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star21: {
    position: 'absolute',
    top: 300,
    left: '82%',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#e8d6ff',
    opacity: 0.18,
    shadowColor: 'rgba(232, 214, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star22: {
    position: 'absolute',
    top: 260,
    left: '42%',
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#dafeff',
    opacity: 0.16,
    shadowColor: 'rgba(218, 254, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star23: {
    position: 'absolute',
    top: 300,
    left: '58%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f9d0ff',
    opacity: 0.18,
    shadowColor: 'rgba(249, 208, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star24: {
    position: 'absolute',
    top: 285,
    left: '34%',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#d3f1ff',
    opacity: 0.18,
    shadowColor: 'rgba(211, 241, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star25: {
    position: 'absolute',
    top: 280,
    left: '22%',
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#f7c5ff',
    opacity: 0.16,
    shadowColor: 'rgba(247, 197, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star26: {
    position: 'absolute',
    top: 255,
    left: '66%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#daf9ff',
    opacity: 0.16,
    shadowColor: 'rgba(218, 249, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star27: {
    position: 'absolute',
    top: 220,
    left: '45%',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#e0f0ff',
    opacity: 0.2,
    shadowColor: 'rgba(224, 240, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star28: {
    position: 'absolute',
    top: 250,
    left: '50%',
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#f0d7ff',
    opacity: 0.16,
    shadowColor: 'rgba(240, 215, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star29: {
    position: 'absolute',
    top: 220,
    left: '50%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#cff8ff',
    opacity: 0.18,
    shadowColor: 'rgba(207, 248, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star30: {
    position: 'absolute',
    top: 300,
    left: '42%',
    width: 9,
    height: 9,
    borderRadius: 4.5,
    backgroundColor: '#e7d7ff',
    opacity: 0.18,
    shadowColor: 'rgba(231, 215, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  star31: {
    position: 'absolute',
    top: 330,
    right: '45%',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e5f1ff',
    opacity: 0.16,
    shadowColor: 'rgba(229, 241, 255, 0.2)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  star32: {
    position: 'absolute',
    top: 260,
    right: '47%',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#f8e6ff',
    opacity: 0.18,
    shadowColor: 'rgba(248, 230, 255, 0.22)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
  },
  starShape1: {
    position: 'absolute',
    top: 190,
    left: 92,
    fontSize: 24,
    color: '#d2b3ff',
    textShadowColor: 'rgba(210, 179, 255, 0.3)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape2: {
    position: 'absolute',
    top: 170,
    right: 60,
    fontSize: 28,
    color: '#b5f0ff',
    textShadowColor: 'rgba(181, 240, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape3: {
    position: 'absolute',
    top: 220,
    left: 45,
    fontSize: 20,
    color: '#ffb5f7',
    textShadowColor: 'rgba(255, 181, 247, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape4: {
    position: 'absolute',
    top: 305,
    right: 168,
    fontSize: 22,
    color: '#c2f9e3',
    textShadowColor: 'rgba(194, 249, 227, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape5: {
    position: 'absolute',
    top: 205,
    left: 180,
    fontSize: 18,
    color: '#f8c9ff',
    textShadowColor: 'rgba(248, 201, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape6: {
    position: 'absolute',
    top: 220,
    right: 20,
    fontSize: 16,
    color: '#d7f0ff',
    textShadowColor: 'rgba(215, 240, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape7: {
    position: 'absolute',
    top: 320,
    left: 100,
    fontSize: 20,
    color: '#e4d3ff',
    textShadowColor: 'rgba(228, 211, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape8: {
    position: 'absolute',
    top: 90,
    left: 260,
    fontSize: 20,
    color: '#eff2ff',
    textShadowColor: 'rgba(239, 242, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape9: {
    position: 'absolute',
    top: 280,
    right: 140,
    fontSize: 18,
    color: '#ffdef3',
    textShadowColor: 'rgba(255, 222, 243, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape10: {
    position: 'absolute',
    top: 190,
    left: 260,
    fontSize: 22,
    color: '#d4ffec',
    textShadowColor: 'rgba(212, 255, 236, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape11: {
    position: 'absolute',
    top: 190,
    right: 90,
    fontSize: 20,
    color: '#f8d8ff',
    textShadowColor: 'rgba(248, 216, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape12: {
    position: 'absolute',
    top: 330,
    left: 20,
    fontSize: 18,
    color: '#d8f4ff',
    textShadowColor: 'rgba(216, 244, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape13: {
    position: 'absolute',
    top: 145,
    left: 205,
    fontSize: 20,
    color: '#f1d8ff',
    textShadowColor: 'rgba(241, 216, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape14: {
    position: 'absolute',
    top: 250,
    right: 90,
    fontSize: 18,
    color: '#cde7ff',
    textShadowColor: 'rgba(205, 231, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape15: {
    position: 'absolute',
    top: 160,
    left: '48%',
    fontSize: 20,
    color: '#ffe6f6',
    textShadowColor: 'rgba(255, 230, 246, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape16: {
    position: 'absolute',
    top: 260,
    right: '44%',
    fontSize: 18,
    color: '#d5f9ff',
    textShadowColor: 'rgba(213, 249, 255, 0.28)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape14: {
    position: 'absolute',
    top: 250,
    right: 90,
    fontSize: 18,
    color: '#f1dfc5',
    textShadowColor: 'rgba(241, 223, 197, 0.6)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starShape15: {
    position: 'absolute',
    top: 310,
    left: 220,
    fontSize: 22,
    color: '#efe5c8',
    textShadowColor: 'rgba(239, 229, 200, 0.6)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  starBar: {
    position: 'absolute',
    width: 2,
    height: 18,
    borderRadius: 1,
    backgroundColor: '#f4e3c6',
  },
  starBarRotated: {
    transform: [{ rotate: '90deg' }],
  },
  historySection: {
    width: '100%',
    marginBottom: 18,
  },
  historyTitle: {
    color: '#f8e7d2',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  historyEmpty: {
    color: '#b9a67a',
    fontSize: 16,
    fontFamily: 'serif',
  },
  historyItem: {
    backgroundColor: '#231b39',
    borderRadius: 18,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#3c2d5c',
  },
  historyText: {
    color: '#ede0cf',
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'serif',
  },
  supportBox: {
    width: '100%',
    backgroundColor: '#111111',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#2f2f33',
    padding: 20,
    marginBottom: 18,
  },
  supportTitle: {
    color: '#f8f8f8',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 10,
    fontFamily: 'PlayfairDisplay_700Bold',
  },
  supportText: {
    color: '#c7c3b9',
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'PlayfairDisplay_400Regular',
  },
  clearButton: {
    borderColor: '#b589d6',
    borderWidth: 1,
    borderRadius: 18,
    paddingVertical: 14,
    paddingHorizontal: 32,
    width: '100%',
    alignItems: 'center',
    marginBottom: 24,
  },
  clearButtonText: {
    color: '#f8e7d2',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'serif',
  },
  footer: {
    color: '#5a5a5a',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'serif',
  },
});